import {
  fetchPublicBranding,
  fetchPublicDemo,
  fetchPublicWidgets,
  sanitiseBrandVars,
  sendPublicChat,
  sendPublicHandover,
  sendPublicMode,
  sendPublicContact,
  ERR_INVALID_CLIENT_TOKEN,
  ERR_HANDOFF_ACTIVE,
  ERR_MODE_CLOSED,
  ERR_CONTACT_INVALID_EMAIL,
  ERR_CONTACT_CLOSED,
  MAX_CONTACT_EMAIL_LEN,
  type AgentResponse,
  type AgentRichCard,
  type AgentSuggestion,
  type HandoffMode,
  type HandoffPayload,
  type PublicBranding,
  type PublicChatResponse,
  type PublicDemo,
} from "./branding";
import { renderMarkdown } from "./markdown";
import {
  resolveLocale,
  disclosureText,
  fallbackGreeting,
  transportErrorText,
  composerPlaceholder,
  type WidgetLocale,
} from "./disclosure";
import { openConversationStream, type StreamMessage, type StreamStateUpdate } from "./stream";

// <chatmerce-chat tenant="acme" api-base="https://api.chatmerce.eu">
//
// Floating chat widget for embed on tenant sites. Renders inside a
// Shadow DOM so Tailwind / framework CSS on the host page can't leak in
// and our styles can't leak out. Initial branding is fetched lazily on
// first interaction so the host page's initial paint isn't blocked.
//
// Phase 4 ships UI + branding fetch + a mock chat. The real streaming
// chat endpoint (SSE → Researcher/Writer pipeline) lands once the
// dispatcher for the agent path exists; until then the user message
// echoes back through a friendly placeholder.
//
// Task 19 (human takeover, spec §6/§7) adds a third message role:
// "human" is a support agent's own reply, pushed live over stream.ts
// once a person takes the conversation over. See ensureStream() below.

type Message = { role: "user" | "agent" | "human"; text: string; author?: string };

// Storage keys are per-tenant and — since Task 19 — live in
// localStorage, not sessionStorage: a closed tab must not orphan a
// conversation a support agent is about to reply to (spec §6). A
// client-side TTL is the privacy trade-off that buys back what
// sessionStorage's tab-lifetime used to give for free on a shared
// device — see expireStaleStorage() and STORAGE_TTL_MS.
const STORAGE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days.

function sessionKeyFor(tenant: string): string {
  return `chatmerce-widget-session-${tenant}`;
}
function conversationKeyFor(tenant: string): string {
  return `chatmerce-widget-conv-${tenant}`;
}
function tokenKeyFor(tenant: string): string {
  return `chatmerce-widget-token-${tenant}`;
}
function touchedKeyFor(tenant: string): string {
  return `chatmerce-widget-touched-${tenant}`;
}

// Localized "a person is helping you" composer banner text. Kept here
// rather than in disclosure.ts: it is new, narrowly-scoped UI copy, not
// part of the always-on AI-disclosure line that module owns.
const HUMAN_BANNER_TEXT: Record<WidgetLocale, { waiting: string; active: string }> = {
  pl: { waiting: "Czekasz na konsultanta…", active: "Rozmawiasz teraz z konsultantem." },
  en: {
    waiting: "Waiting for a support agent…",
    active: "You're now chatting with a support agent.",
  },
  no: { waiting: "Venter på en kundekonsulent…", active: "Du chatter nå med en kundekonsulent." },
};
function humanBannerText(locale: WidgetLocale, state: string): string {
  const t = HUMAN_BANNER_TEXT[locale] ?? HUMAN_BANNER_TEXT.pl;
  return state === "active" ? t.active : t.waiting;
}

// Task 16: waiting state, contact capture and the wait timeout (design
// spec §4/§13). WAIT_TIMEOUT_MS is client-side only, by explicit M1b
// decision — no server sweep. That is deliberately narrower than M2's
// eventual unclaimed-ticket timer, which this must not pull forward: a
// tab that's gone (closed, backgrounded past a throttle, offline) never
// fires this at all, and that's fine — the escape hatch it exists to
// offer is for a visitor who is STILL LOOKING at the panel with nobody
// answering, not a server-side ticket SLA.
const WAIT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes.

// A permissive, client-side-only shape check — "posts nothing" (case 3)
// means the form must reject an obviously malformed address before ever
// calling contactSender, not that this regex is the last word on what a
// valid email looks like. The server (mail.ParseAddress, public_handoff.go
// postContact) remains the real validator; this only spares the visitor
// a round trip for the common typo.
const CONTACT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Contact-form copy (Task 16, design spec §13's fallback-address nudge).
// The PROMPT line is only the fallback: renderContactForm() prefers the
// server's own contact_prompt (PublicChatResponse.contact_prompt) when
// this turn's response carried one — the wait/continue chip's own
// POST …/mode never does (ModeResponseSchema has no such field), so a
// visitor who reaches "waiting" by tapping the chip always sees this
// local copy instead.
const CONTACT_FORM_TEXT: Record<
  WidgetLocale,
  {
    prompt: string;
    placeholder: string;
    submit: string;
    skip: string;
    invalidEmail: string;
    confirmation: string;
    // Fix Round 1, Important 2: the ticket is gone (404) or already
    // closed (409) — a retry is structurally impossible, unlike a
    // 429/5xx, so this gets its own copy rather than transportErrorText's
    // generic "try again".
    closed: string;
  }
> = {
  pl: {
    prompt: "Zostaw e-mail, a napiszemy do Ciebie, jeśli zamkniesz to okno.",
    placeholder: "twoj@e-mail.pl",
    submit: "Wyślij",
    skip: "Nie teraz",
    invalidEmail: "Podaj prawidłowy adres e-mail.",
    confirmation: "Dzięki! Napiszemy na ten adres, jeśli nie zdążymy odpowiedzieć tutaj.",
    closed: "Ta rozmowa została już zakończona.",
  },
  en: {
    prompt: "Leave your email and we'll reach you there if you close this window.",
    placeholder: "you@example.com",
    submit: "Send",
    skip: "Not now",
    invalidEmail: "Enter a valid email address.",
    confirmation: "Thanks! We'll email you there if we can't reply here in time.",
    closed: "This conversation has already been closed.",
  },
  no: {
    prompt: "Legg igjen e-posten din, så tar vi kontakt der hvis du lukker vinduet.",
    placeholder: "din@epost.no",
    submit: "Send",
    skip: "Ikke nå",
    invalidEmail: "Skriv inn en gyldig e-postadresse.",
    confirmation: "Takk! Vi sender deg en e-post dit hvis vi ikke rekker å svare her i tide.",
    closed: "Denne samtalen er allerede avsluttet.",
  },
};
function contactFormCopy(locale: WidgetLocale): (typeof CONTACT_FORM_TEXT)["pl"] {
  return CONTACT_FORM_TEXT[locale] ?? CONTACT_FORM_TEXT.pl;
}

// The wait-timeout offer's copy (Task 16, case 6/7/8). This is the ONLY
// way back from "waiting" that existed before /mode did — see the
// offer's own render method for why "Go back to the assistant" is the
// whole point of the timeout, not a minor convenience.
const WAIT_TIMEOUT_TEXT: Record<
  WidgetLocale,
  { message: string; backToAssistant: string; keepWaiting: string }
> = {
  pl: {
    message: "Nadal czekasz na konsultanta. Wrócić do asystenta, czy czekać dalej?",
    backToAssistant: "Wróć do asystenta",
    keepWaiting: "Czekaj dalej",
  },
  en: {
    message: "Still waiting for a support agent. Go back to the assistant, or keep waiting?",
    backToAssistant: "Go back to the assistant",
    keepWaiting: "Keep waiting",
  },
  no: {
    message: "Fortsatt venter på en kundekonsulent. Gå tilbake til assistenten, eller vent videre?",
    backToAssistant: "Gå tilbake til assistenten",
    keepWaiting: "Fortsett å vente",
  },
};
function waitTimeoutCopy(locale: WidgetLocale): (typeof WAIT_TIMEOUT_TEXT)["pl"] {
  return WAIT_TIMEOUT_TEXT[locale] ?? WAIT_TIMEOUT_TEXT.pl;
}

const NEW_CONVERSATION_LABEL: Record<WidgetLocale, string> = {
  pl: "Nowa rozmowa",
  en: "New conversation",
  no: "Ny samtale",
};

// The "Talk to a human" affordance's own label (Task 15, design spec §8's
// third way a ticket opens: "the customer asks explicitly"). A persistent
// control (Fix Round 1 product ruling), not a one-shot chip — its click
// handler calls POST …/handover directly (sendPublicHandover) instead of
// send() whenever a conversation already exists, so a tap costs no LLM
// turn and no quota.
//
// These MUST stay byte-identical (case aside — the server's normalize()
// lowercases) to handoff.TriggerPhrase (apps/api/internal/handoff/
// message.go): requestHandover() falls back to send()-ing this exact
// text when no conversation exists yet to target /handover (a fresh
// visitor's very first action), and public_demo.go's keyword
// short-circuit (handoff.Requested) only matches an EXACT normalized
// phrase — no fuzzy matching. Fix Round 1, Important 1: pl/no here used
// to be paraphrases ("z konsultantem" / "en kundekonsulent") that did not
// match the server's phrase set at all, so that fallback — and a visitor
// simply TYPING the button's own label instead of tapping it — silently
// fell through to a billed LLM turn.
const HANDOFF_AFFORDANCE_LABEL: Record<WidgetLocale, string> = {
  pl: "Porozmawiaj z człowiekiem",
  en: "Talk to a human",
  no: "Snakk med et menneske",
};
function handoffAffordanceLabel(locale: WidgetLocale): string {
  return HANDOFF_AFFORDANCE_LABEL[locale] ?? HANDOFF_AFFORDANCE_LABEL.pl;
}

// Launcher icons. This bundle is one file for every tenant, so the default
// has to be brand-neutral. It used to be a 💬 emoji, which each OS draws in
// its own house style — a glossy blue blob on macOS, a flat green rectangle
// on Android — so no tenant ever got the icon they picked. A stroke bubble
// renders identically everywhere and inherits the tenant's colour.
//
// Geometry matches Icons.Chat in apps/web/components/ui/icons.tsx so the
// dashboard preview and the live widget draw the same bubble.
const ICON_BUBBLE = `
<svg viewBox="0 0 24 24" aria-hidden="true">
  <path class="stroke" d="M21 12a8 8 0 01-11.7 7.1L4 21l1.9-5.3A8 8 0 1121 12z" />
</svg>`;

// The Chatmerce mark, opt-in behind icon="mark" and used only on our own
// sites: shipping it as the default would stamp our logo onto every
// customer's launcher, which the white-label tiers explicitly pay not to
// have. Drawn in currentColor so it inherits --brand-primary_fg like the
// bubble. Geometry mirrors Logo.astro on the marketing site; keep in sync.
const ICON_MARK = `
<svg viewBox="0 0 32 32" aria-hidden="true" data-glyph="mark">
  <rect x="2" y="9" width="18" height="18" rx="5" fill="currentColor" opacity="0.55" />
  <rect x="12" y="3" width="18" height="18" rx="5" fill="currentColor" />
  <path d="M6 27 L6 23 L10 27 Z" fill="currentColor" opacity="0.55" />
</svg>`;

const ICON_CLOSE = `<svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
  <path class="stroke" d="M18 6L6 18M6 6l12 12" /></svg>`;

const ICON_SEND = `<svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
  <path class="stroke" d="M12 19V5M5 12l7-7 7 7" /></svg>`;

// A plus-in-circle: "start over". Styled by its own rule below rather
// than the (header-scoped, pre-existing) .stroke convention, so it is
// guaranteed visible regardless of that convention's own CSS coverage.
const ICON_RESET = `<svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
  <circle cx="12" cy="12" r="9" /><path d="M12 8v8M8 12h8" /></svg>`;

const HOST_TEMPLATE = `
<style>
  :host {
    --brand-primary: #2563eb;
    --brand-primary_fg: #ffffff;
    --brand-accent: #10b981;
    --brand-accent_fg: #ffffff;
    --brand-bg: #ffffff;
    --brand-fg: #111827;
    --brand-muted: #f3f4f6;
    --brand-border: #e5e7eb;
    --brand-font: ui-sans-serif, system-ui, -apple-system, "Segoe UI",
      Roboto, sans-serif;
    /* Launcher geometry is a custom property so an embedding page can
       match its own shape language (a squircle, a larger tap target)
       without us shipping a per-tenant stylesheet. Defaults reproduce
       the circle the widget has always drawn. */
    --brand-launcher-size: 56px;
    --brand-launcher-radius: 50%;
    --brand-launcher-shadow: 0 1px 1px rgba(0, 0, 0, 0.04),
      0 8px 20px -6px rgba(0, 0, 0, 0.22),
      0 18px 40px -18px rgba(0, 0, 0, 0.28);

    position: fixed;
    right: 1rem;
    bottom: 1rem;
    z-index: 2147483646;
    font-family: var(--brand-font);
    color: var(--brand-fg);
    line-height: 1.4;
  }

  .launcher {
    width: var(--brand-launcher-size);
    height: var(--brand-launcher-size);
    border-radius: var(--brand-launcher-radius);
    border: 0;
    padding: 0;
    background: var(--brand-primary);
    color: var(--brand-primary_fg);
    cursor: pointer;
    /* The inset hairline keeps the button from dissolving into a
       same-hue page background; the layered shadow reads as depth
       rather than the single flat blur we drew before. */
    box-shadow: var(--brand-launcher-shadow),
      inset 0 0 0 1px rgba(255, 255, 255, 0.12);
    display: grid;
    place-items: center;
    transition:
      transform 160ms cubic-bezier(0.2, 0.7, 0.3, 1),
      box-shadow 160ms ease;
  }
  .launcher:hover { transform: translateY(-2px); }
  .launcher:active { transform: translateY(0); }
  .launcher:focus-visible {
    outline: 2px solid var(--brand-primary);
    outline-offset: 3px;
  }
  /* Both icons occupy the same grid cell, so open/close is a cross-fade
     in place instead of a swap that reflows the button. */
  .launcher > * {
    grid-area: 1 / 1;
    transition: opacity 140ms ease, transform 140ms ease;
  }
  .launcher .icon-close { opacity: 0; transform: rotate(-45deg) scale(0.85); }
  :host([open]) .launcher .icon-open {
    opacity: 0;
    transform: rotate(45deg) scale(0.85);
  }
  :host([open]) .launcher .icon-close { opacity: 1; transform: none; }
  .launcher svg { display: block; width: 26px; height: 26px; }
  /* The mark is a filled glyph rather than a stroke one, so it needs a
     couple more pixels to carry the same optical weight as the bubble. */
  .launcher [data-glyph="mark"] { width: 28px; height: 28px; }
  .launcher .icon-close { display: grid; place-items: center; }
  .launcher .icon-close svg { width: 24px; height: 24px; }
  .launcher .stroke {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.75;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .launcher img {
    width: 28px;
    height: 28px;
    object-fit: contain;
    border-radius: 6px;
  }
  @media (prefers-reduced-motion: reduce) {
    .launcher, .launcher > * { transition: none; }
    .launcher:hover, .launcher:active { transform: none; }
  }

  .panel {
    position: absolute;
    right: 0;
    /* Tracks the launcher, so a page that enlarges the button doesn't
       end up with the panel overlapping it. */
    bottom: calc(var(--brand-launcher-size) + 16px);
    width: 380px;
    /* Fixed height (not just max-height) so the panel doesn't collapse
       to header+composer before the first greeting renders. The
       messages list still flex:1's inside, so the visible region grows
       with content up to this cap. */
    height: 560px;
    max-height: calc(100vh - 6rem);
    background: var(--brand-bg);
    border: 1px solid var(--brand-border);
    border-radius: 22px;
    box-shadow:
      0 32px 64px -24px rgba(0, 0, 0, 0.35),
      0 4px 12px -6px rgba(0, 0, 0, 0.2);
    display: none;
    flex-direction: column;
    overflow: hidden;
  }
  :host([open]) .panel { display: flex; }
  /* On a phone the panel is a sheet anchored to the bottom edge rather
     than a floating card with a stripe of page showing around it. The
     launcher would sit on top of the composer, so it steps aside — the
     header's close button is the way out. */
  @media (max-width: 480px) {
    .panel {
      position: fixed;
      inset: auto 0 0 0;
      width: 100vw;
      height: 88vh;
      max-height: none;
      border-radius: 22px 22px 0 0;
      border-bottom: 0;
    }
    :host([open]) .launcher { display: none; }
    /* The sheet leaves a strip of page showing, which looks tappable and
       until now wasn't: the header's cross was the only way out. Dimming
       that strip makes it read as pushed back and gives it the job it
       already looked like it had. Phone only — on a desktop the panel is
       a calm window beside the content, not a modal over it, and the
       widget has no business painting outside its own corner there. */
    :host([open]) .scrim {
      display: block;
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.45);
      animation: chatmerce-fade 160ms ease;
    }
  }
  .scrim { display: none; }
  @keyframes chatmerce-fade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    :host([open]) .scrim { animation: none; }
  }

  header {
    background: var(--brand-bg);
    color: var(--brand-fg);
    padding: 18px 20px 14px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .head-avatar {
    width: 26px;
    height: 26px;
    flex: none;
    border-radius: 8px;
    display: grid;
    place-items: center;
    background: color-mix(in srgb, var(--brand-primary) 14%, transparent);
    color: var(--brand-primary);
  }
  .head-avatar > * { width: 15px; height: 15px; display: block; }
  .head-avatar img { object-fit: contain; border-radius: 4px; }
  header .title {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.01em;
    line-height: 1.25;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  header button.close, header button.new-conv {
    background: transparent;
    border: 0;
    color: var(--brand-fg);
    opacity: 0.55;
    cursor: pointer;
    padding: 5px;
    margin: -5px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    transition: opacity 120ms ease, background 120ms ease;
  }
  header button.close:hover, header button.new-conv:hover {
    opacity: 1;
    background: var(--brand-muted);
  }
  header button.close:focus-visible, header button.new-conv:focus-visible {
    outline: 2px solid var(--brand-primary);
    outline-offset: 1px;
  }
  /* Unlike .close/.send, this icon's paths are drawn as actual open
     shapes (a circle + a plus), so they need an explicit stroke rather
     than the header's shared .stroke convention. */
  header button.new-conv svg {
    fill: none;
    stroke: currentColor;
    stroke-width: 1.75;
    stroke-linecap: round;
  }
  .icon {
    display: block;
    width: 18px;
    height: 18px;
  }

  /* The always-on banner while a person owns the thread (spec §7/§9).
     Sits between the header and the transcript so it reads before any
     message, on both a fresh takeover and a reload that restores one. */
  .human-banner {
    padding: 6px 20px;
    font-size: 12px;
    font-weight: 600;
    text-align: center;
    color: var(--brand-primary);
    background: color-mix(in srgb, var(--brand-primary) 10%, transparent);
  }
  .human-banner[hidden] { display: none; }

  /* The persistent "Talk to a human" control (Task 15, Fix Round 1
     product ruling): visible whenever support_handoff_enabled is true
     and no human is already involved, so it survives however many
     ordinary turns a visitor has already had — unlike a one-shot
     suggestion chip, it is not tied to the greeting and does not vanish
     the moment the visitor types anything. Sits right above the
     composer, outside the scrollable transcript, so it never needs
     scrolling into view. */
  .handoff-cta {
    padding: 0 20px 6px;
  }
  .handoff-cta[hidden] { display: none; }
  .handoff-cta .chip { padding: 4px 0; }

  .messages {
    flex: 1;
    overflow-y: auto;
    padding: 6px 20px 14px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    background: var(--brand-bg);
    scrollbar-width: thin;
    /* Without this, a drag inside a list that has nothing left to scroll
       carries on into the host page — which on the mobile sheet looks
       like the widget dragging the site around behind itself. */
    overscroll-behavior: contain;
  }

  /* One agent turn: a small glyph and the reply beside it. The glyph is
     what marks the text as the agent's, which is why the reply itself
     needs no bubble — see .bubble.agent below. */
  .turn {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    align-self: stretch;
  }
  .turn-avatar {
    width: 24px;
    height: 24px;
    flex: none;
    margin-top: 1px;
    border-radius: 7px;
    display: grid;
    place-items: center;
    background: var(--brand-muted);
    border: 1px solid var(--brand-border);
  }
  .turn-avatar > * { width: 13px; height: 13px; display: block; }
  .turn-avatar img { object-fit: contain; border-radius: 3px; }
  .turn-avatar .stroke, .turn-avatar [data-glyph="mark"] {
    color: var(--brand-fg);
    opacity: 0.5;
  }

  .bubble {
    font-size: 14px;
    word-wrap: break-word;
    overflow-wrap: anywhere;
  }
  /* The user is placed by alignment and a hairline, not by a slab of
     brand colour: two saturated bubbles per screen is what made the old
     panel shout. */
  .bubble.user {
    align-self: flex-end;
    max-width: 80%;
    padding: 9px 14px;
    border: 1px solid var(--brand-border);
    border-radius: 16px 16px 5px 16px;
    color: var(--brand-fg);
    line-height: 1.5;
  }
  /* The agent's reply is plain text on the panel's own background. It
     reads as prose, which is what a paragraph of RAG answer actually
     is, and it stops long replies from becoming a grey wall. */
  .bubble.agent {
    flex: 1;
    min-width: 0;
    font-size: 14.5px;
    line-height: 1.6;
    color: var(--brand-fg);
  }
  .bubble.agent > p { margin: 0 0 8px; }
  .bubble.agent > p:last-child { margin-bottom: 0; }
  .bubble.agent ul, .bubble.agent ol { margin: 6px 0; padding-left: 18px; }
  .bubble.agent a { color: var(--brand-primary); }
  /* A support agent's own reply (spec §6 RoleHuman). Placed in the same
     left-aligned "turn" as the bot (agentTurn()), but never mistaken
     for one: a tinted card plus the agent's name, instead of plain
     prose on the panel background. */
  .bubble.human {
    flex: 1;
    min-width: 0;
    font-size: 14.5px;
    line-height: 1.6;
    color: var(--brand-fg);
    background: var(--brand-muted);
    border-radius: 12px;
    padding: 8px 12px;
  }
  .bubble.human .human-author {
    font-size: 11.5px;
    font-weight: 700;
    opacity: 0.65;
    margin-bottom: 2px;
  }
  .bubble.human > p { margin: 0 0 8px; }
  .bubble.human > p:last-child { margin-bottom: 0; }
  .bubble.typing {
    display: inline-flex;
    gap: 5px;
    padding: 6px 0;
  }
  .bubble.typing span {
    width: 6px; height: 6px; border-radius: 999px;
    background: var(--brand-fg);
    opacity: 0.4;
    animation: chatmerce-typing 1.2s infinite ease-in-out;
  }
  .bubble.typing span:nth-child(2) { animation-delay: 0.15s; }
  .bubble.typing span:nth-child(3) { animation-delay: 0.3s; }
  @keyframes chatmerce-typing {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.3; }
    40% { transform: scale(1); opacity: 0.9; }
  }

  /* Suggestions sit under the agent's reply, indented to the reply's
     text column (24px glyph + 11px gap) so they read as a continuation
     of that turn rather than a toolbar. */
  .suggestions, .chips {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    align-self: stretch;
    margin-top: -8px;
    padding-left: 35px;
  }

  /* The contact form (Task 16, case 1/2/3/4) and the wait-timeout offer
     (case 6/7/8) both sit indented under the agent turn column, same as
     .suggestions/.chips above — they read as a continuation of the
     waiting-state conversation, not a separate panel. */
  .contact-form, .wait-timeout-offer {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    padding-left: 35px;
  }
  .contact-form-prompt, .wait-timeout-message {
    margin: 0;
    font-size: 13px;
    line-height: 1.4;
    color: var(--brand-fg);
    opacity: 0.8;
  }
  .contact-form-row {
    display: flex;
    align-items: center;
    gap: 6px;
    align-self: stretch;
  }
  .contact-form-row input {
    flex: 1;
    min-width: 0;
    padding: 6px 10px;
    border: 1px solid var(--brand-border);
    border-radius: 10px;
    background: var(--brand-bg);
    color: var(--brand-fg);
    font: inherit;
    font-size: 13px;
  }
  .contact-form-row input:focus { outline: 2px solid var(--brand-primary); outline-offset: 1px; }
  .contact-form-row input[disabled] { opacity: 0.6; }
  .contact-form .chip, .wait-timeout-offer .chip { padding: 4px 0; }
  .contact-form-error {
    margin: -4px 0 0;
    font-size: 12px;
    color: #b91c1c;
  }
  .contact-form-error[hidden] { display: none; }
  /* Fix Round 1, Minor 4: these are appended to .messages directly, NOT
     inside .contact-form (which clearWaitTimeoutState() removes whole
     on leaving "waiting") — see submitContactEmail()'s own doc comment
     for why the visitor's confirmation (and the "conversation closed"
     note) have to outlive the form itself. padding-left reproduces the
     indent .contact-form's own rule gave them for free while nested. */
  .contact-form-confirmation, .contact-form-closed {
    margin: 0;
    padding-left: 35px;
    font-size: 13px;
    line-height: 1.4;
    color: var(--brand-fg);
    opacity: 0.8;
  }
  .richcard {
    background: var(--brand-bg);
    border: 1px solid var(--brand-border);
    border-radius: 14px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    align-self: stretch;
    /* Defence-in-depth: if a content branch produces empty
       inner-html (broken image + missing body text), at least
       the card outline remains visibly card-shaped instead of
       collapsing to its 1px border. */
    min-height: 200px;
  }
  .richcard.carousel {
    flex: 0 0 220px;
    scroll-snap-align: start;
  }
  .richcard-img {
    width: 100%;
    /* Explicit height (not aspect-ratio) so the image area renders
       a real box even when the carousel's flex-basis sizing
       confuses aspect-ratio + width:100% (some browsers compute
       0 height in that combo). */
    height: 140px;
    object-fit: cover;
    display: block;
    background: var(--brand-muted);
  }
  .richcard-placeholder {
    width: 100%;
    height: 140px;
    background: linear-gradient(135deg, var(--brand-accent), var(--brand-primary));
    /* Was opacity: 0.25 — invisible against muted brand palettes.
       0.85 keeps a hint of softness while still reading as a
       distinct image-area block. */
    opacity: 0.85;
  }
  .richcard-body { padding: 12px 14px 14px; }
  .richcard-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--brand-fg);
    line-height: 1.3;
  }
  .richcard-desc {
    margin: 6px 0 0;
    font-size: 12.5px;
    line-height: 1.45;
    color: var(--brand-fg);
    opacity: 0.78;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .richcard-actions {
    margin-top: 10px;
    display: flex; flex-wrap: wrap; gap: 6px;
  }
  .carousel {
    display: flex; gap: 10px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding-bottom: 8px;
    align-self: stretch;
  }
  .carousel::-webkit-scrollbar { display: none; }
  /* Drawn as links, not pills. The vertical padding is deliberate: it
     buys a ~35px touch target while keeping the link look, so a thumb
     on a phone still lands on it. */
  .chip {
    appearance: none;
    background: none;
    border: 0;
    color: var(--brand-primary);
    font: inherit;
    font-size: 13.5px;
    padding: 8px 0;
    cursor: pointer;
    text-align: left;
    text-decoration: underline;
    text-underline-offset: 3px;
    text-decoration-color: color-mix(in srgb, var(--brand-primary) 35%, transparent);
    transition: text-decoration-color 120ms ease;
  }
  .chip:hover { text-decoration-color: currentColor; }
  .chip:active { opacity: 0.7; }
  .chip:focus-visible {
    outline: 2px solid var(--brand-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
  /* Inside a rich card the actions are the card's own buttons, so they
     keep a bounded, tappable shape instead of turning into links. */
  .richcard-actions .chip {
    padding: 7px 12px;
    border-radius: 10px;
    text-decoration: none;
    font-size: 13px;
    font-weight: 500;
    background: color-mix(in srgb, var(--brand-primary) 12%, transparent);
  }

  /* No rule above the composer — the island's own edge is enough
     separation, and one less horizontal line is one less thing dividing
     a 380px-wide panel into strips. */
  form.composer {
    padding: 12px;
    background: var(--brand-bg);
  }
  .composer-island {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 7px 7px 7px 15px;
    border: 1px solid var(--brand-border);
    border-radius: 16px;
    background: var(--brand-muted);
    transition: border-color 140ms ease, box-shadow 140ms ease;
  }
  /* Focus lands on the island, not the bare input, so the ring frames
     the field and the send button as the one control they look like. */
  .composer-island:focus-within {
    border-color: var(--brand-primary);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand-primary) 18%, transparent);
  }
  form.composer input {
    flex: 1;
    min-width: 0;
    padding: 0;
    border: 0;
    background: none;
    color: var(--brand-fg);
    font: inherit;
    font-size: 13.5px;
  }
  form.composer input:focus { outline: none; }
  form.composer input::placeholder { color: var(--brand-fg); opacity: 0.45; }
  form.composer button {
    width: 34px;
    height: 34px;
    flex: none;
    padding: 0;
    border: 0;
    border-radius: 11px;
    background: var(--brand-primary);
    color: var(--brand-primary_fg);
    cursor: pointer;
    display: grid;
    place-items: center;
  }
  form.composer button svg { width: 17px; height: 17px; }
  form.composer button[disabled] { opacity: 0.45; cursor: not-allowed; }

  /* Moved below the composer. It used to open the conversation with a
     bordered block of small print, which is the first thing a visitor
     read. It is still on screen without scrolling or interaction, which
     is what Art. 50 asks for, and it is never truncated. */
  .disclosure {
    padding: 0 20px 10px;
    font-size: 11px;
    line-height: 1.45;
    text-align: center;
    /* Uses the readable foreground/background pair (not --brand-muted)
       so a tenant can't hide this always-on disclosure by setting
       muted == bg. To hide it now a tenant would have to set
       fg == bg, which hides ALL widget text — an obvious
       self-defeating misconfiguration, not a targeted disclosure bypass. */
    color: var(--brand-fg, #1a1a1a);
    opacity: 0.72;
    background: var(--brand-bg, #fff);
  }
  .disclosure a { color: inherit; text-underline-offset: 2px; }

  .powered {
    padding: 0 12px 10px;
    font-size: 10.5px;
    line-height: 1.3;
    text-align: center;
    background: var(--brand-bg, #fff);
  }
  /* Paid tiers (and the brief pre-branding window) leave this node
     empty — collapse it entirely instead of rendering a blank strip. */
  .powered:empty { display: none; }
  .powered a {
    /* --brand-fg at low opacity (not --brand-muted) so this stays
       legible regardless of a tenant's muted-swatch choice. 0.7
       resting / ~0.85 hover clears WCAG AA for this small text
       against the --brand-fg/--brand-bg pair. */
    color: var(--brand-fg, #1a1a1a);
    opacity: 0.7;
    text-decoration: none;
  }
  .powered a:hover { opacity: 0.85; text-decoration: underline; }
</style>

<button class="launcher" type="button" aria-label="Open chat">
  <span class="icon-open"></span>
  <span class="icon-close">${ICON_CLOSE}</span>
</button>
<div class="scrim" aria-hidden="true"></div>
<section class="panel" role="dialog" aria-label="Chat">
  <header>
    <span class="head-avatar"><span class="head-glyph"></span></span>
    <span class="title"></span>
    <button class="new-conv" type="button" aria-label="Start a new conversation" title="Start a new conversation">${ICON_RESET}</button>
    <button class="close" type="button" aria-label="Close chat">${ICON_CLOSE}</button>
  </header>
  <div class="human-banner" role="status" hidden></div>
  <div class="messages" role="log" aria-live="polite"></div>
  <div class="handoff-cta" hidden>
    <button type="button" class="chip"></button>
  </div>
  <form class="composer" autocomplete="off">
    <div class="composer-island">
      <input type="text" name="text" required maxlength="500" aria-label="Message" />
      <button type="submit" aria-label="Send">${ICON_SEND}</button>
    </div>
  </form>
  <div class="disclosure"></div>
  <div class="powered"></div>
</section>
`;

export class ChatmerceChat extends HTMLElement {
  static readonly observedAttributes = ["tenant", "api-base", "widget", "open", "lang", "icon"];

  private root: ShadowRoot;
  private launcher!: HTMLButtonElement;
  private panel!: HTMLElement;
  private title!: HTMLElement;
  private disclosureEl!: HTMLElement;
  private poweredEl!: HTMLElement;
  private closeBtn!: HTMLButtonElement;
  private newConvBtn!: HTMLButtonElement;
  private humanBanner!: HTMLElement;
  private messagesEl!: HTMLElement;
  private suggestionsEl: HTMLElement | null = null;
  // The persistent "Talk to a human" control (Fix Round 1 product
  // ruling) — handoffCtaEl is the hidden-toggled wrapper, checked in
  // applyHandoffAffordance(); handoffAffordanceBtn is the button whose
  // label that same method keeps in sync with the resolved locale.
  private handoffCtaEl!: HTMLElement;
  private handoffAffordanceBtn!: HTMLButtonElement;
  private form!: HTMLFormElement;
  private input!: HTMLInputElement;
  private sendBtn!: HTMLButtonElement;

  private brandingLoaded = false;
  private brandingPromise: Promise<PublicBranding | null> | null = null;
  private branding: PublicBranding | null = null;
  private demo: PublicDemo | null = null;
  private agentName: string = "Asystent";
  private suggestions: string[] = [];
  // Welcome text from the tenant's default widget config. Fetched from
  // /api/v1/public/tenants/{slug}/widgets in parallel with /demo. When
  // present it replaces the generic greeting shown on first open.
  private welcomeText: string = "";
  // Welcome quick-reply buttons from the tenant's default widget config.
  // Shown with the greeting; tapping one sends its message as the first turn.
  private welcomeButtons: Array<{ label: string; message: string }> = [];
  private messages: Message[] = [];
  private sessionID: string = "";
  // conversationID scopes the message history server-side. The first
  // chat send leaves it empty — the server mints one and returns it
  // in the response, and we pin it for subsequent requests. After a
  // "resetBot" command the server returns a new id; the widget
  // detects the change, wipes its visible message list, and keeps
  // the new one. The previous thread stays in the dashboard's
  // /conversations view (no destructive delete).
  private conversationID: string = "";
  // clientToken is the per-conversation bearer secret (spec P2),
  // returned once on the turn that mints the conversation and never
  // echoed again. Required (with conversationID) to open the visitor
  // stream — see ensureStream(). "" means either no conversation yet,
  // or a legacy one created before tokens existed.
  private clientToken: string = "";
  // humanState mirrors the conversation's ticket mirror ("" | "pending"
  // | "waiting" | "active"), kept only to skip redundant banner
  // updates — see applyHumanState().
  private humanState: string = "";
  // busy is a SHARED re-entrancy guard between send(), requestHandover()
  // and chooseHandoffMode() (Fix Round 2/3, Important). Before it
  // existed, the composer and the persistent handoff affordance could
  // fire concurrently — a visitor submits a real question, then taps
  // "Talk to a human" while it's still in flight, and because
  // conversationID is still "" mid-flight, requestHandover()'s own
  // cold-start fallback sent a SECOND concurrent /chat with an empty
  // conversation id: the server mints two conversations/tokens/tickets,
  // and the visitor's real question ends up orphaned in a conversation
  // no ticket ever points at. Checked at the top of every method that
  // touches it; setBusy() is the single place that flips it (and every
  // interactive control's disabled state) together — never write
  // `this.busy = …` directly. requestHandover()'s two branches that
  // DELEGATE to send() (Fix Round 2, Minor — this used to say both
  // branches release the flag before delegating; only the post-403
  // catch branch ever acquires it in the first place, so only that one
  // needs to release it — the cold-start branch never sets `busy` at
  // all, since it delegates immediately, before anything could set it)
  // release it via setBusy(false) immediately before handing off, so
  // send()'s own setBusy(true) is the one the delegated call sees.
  private busy = false;
  // streamClose / streamConvID track the currently-open visitor stream
  // so ensureStream() is idempotent per conversation and
  // disconnectedCallback()/startNewConversation() can tear it down.
  private streamClose: (() => void) | null = null;
  private streamConvID: string = "";
  // Component-level dedupe for rendered "human" message ids (Fix Round
  // 2, Item 1 — CRITICAL). stream.ts's own dedupe lives INSIDE one
  // openConversationStream() call and is discarded the instant that
  // instance closes; a remount (connectedCallback's re-open, added in
  // round 1) opens a BRAND NEW instance with cursor="" and an empty
  // `seen` set, so its first catch-up re-delivers every past staff
  // message while the transcript from before the detach is still on
  // screen — this is what actually survives across stream instances.
  //
  // The cap is a MEMORY backstop, not a correctness bound, and it must
  // cover the conversation's whole human-authored HISTORY — not one
  // catch-up page. An earlier version bounded it to the server's page
  // limit (catchUpLimit = 200, conv/store.go) by analogy with stream.ts's
  // SEEN_MAX; that rationale was wrong, and self-defeating. Eviction is
  // oldest-inserted-first, while a remount's fresh catch-up starts from
  // cursor="" — the zero time — and ListMessagesAfter orders ASCENDING
  // with Limit(200), so it replays the OLDEST ≤200 ids: precisely the
  // ones a 200-entry FIFO had just evicted. Past 200 human messages in
  // one conversation, a remount would re-render those bubbles.
  // 10000 is far beyond any plausible support thread (real ones run to
  // dozens of staff messages), and it costs nothing next to the DOM
  // already held for every one of those bubbles — a few hundred KB of
  // short ids in the pathological case the cap exists for at all.
  //
  // The proper fix is cursor-resume on remount (persist the last-seen
  // created_at alongside the conversation identity, so a re-open asks
  // for messages AFTER it instead of replaying from zero), which makes
  // the cross-instance dedupe set unnecessary rather than merely large.
  // That is M1b work — it touches the widget's persisted identity
  // shape, which M1a's takeover flow does not otherwise change.
  private static readonly RENDERED_ID_MAX = 10000;
  private renderedMessageIdOrder: string[] = [];
  private renderedMessageIds = new Set<string>();

  // The wait/continue chip row from the most recent handoff payload —
  // one-shot, unlike the persistent affordance above: removed the moment
  // one is clicked (case 7) and never resurrected by a later reply that
  // carries no `handoff` of its own. Tracked separately from
  // suggestionsEl so a suggestion-chip click elsewhere never removes it,
  // and vice versa.
  private handoffChipsEl: HTMLElement | null = null;

  // Task 16: the wait-timeout escape hatch (case 6-11). Armed the
  // instant humanState becomes "waiting" (applyHumanState()), cancelled
  // the instant it leaves "waiting" by ANY route — a resolved mode
  // change, a stream state update, or discardConversationIdentity()'s
  // own reset — so it can never fire against a conversation that has
  // already moved on (case 11). Also cancelled the instant a human
  // message arrives, independent of whether/when a "state" frame
  // follows it (case 9) — see onStreamMessage().
  private waitTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  // The rendered "still waiting? go back or keep waiting" offer
  // (case 6-8). One-shot per firing, same convention as
  // handoffChipsEl: removed the moment either button is tapped, or the
  // instant the conversation leaves "waiting" by any other route.
  private waitTimeoutOfferEl: HTMLElement | null = null;
  // The rendered contact-capture form (case 1-4). Same one-shot-per-
  // waiting-entry convention as the fields above.
  private contactFormEl: HTMLElement | null = null;
  // The most recent contact_prompt from a /chat response (Task 10),
  // consumed by renderContactForm() and cleared the instant the
  // conversation leaves "waiting" — see clearWaitTimeoutState(). Reset
  // to "" at the top of chooseHandoffMode() too: the wait/continue
  // chip's own POST …/mode response never carries this field, so a
  // stale value from an EARLIER waiting episode must never leak into a
  // later one reached by tapping the chip instead.
  private contactPromptText = "";

  // Override for tests so a fake fetch can be injected.
  fetcher: typeof fetchPublicBranding = fetchPublicBranding;
  demoFetcher: typeof fetchPublicDemo = fetchPublicDemo;
  widgetsFetcher: typeof fetchPublicWidgets = fetchPublicWidgets;
  chatSender: typeof sendPublicChat = sendPublicChat;
  handoverSender: typeof sendPublicHandover = sendPublicHandover;
  modeSender: typeof sendPublicMode = sendPublicMode;
  contactSender: typeof sendPublicContact = sendPublicContact;
  streamOpener: typeof openConversationStream = openConversationStream;
  // Injectable timer for the wait-timeout (case 6-11), following
  // stream.ts's own OpenStreamOptions pattern (setTimeoutImpl/
  // clearTimeoutImpl) — a test must never sleep for a real 5-minute
  // timer to observe the offer.
  setTimeoutImpl: (fn: () => void, ms: number) => ReturnType<typeof setTimeout> = (fn, ms) =>
    setTimeout(fn, ms);
  clearTimeoutImpl: (id: ReturnType<typeof setTimeout>) => void = (id) => clearTimeout(id);

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
    this.root.innerHTML = HOST_TEMPLATE;
    this.launcher = this.root.querySelector(".launcher")!;
    this.panel = this.root.querySelector(".panel")!;
    this.title = this.root.querySelector(".title")!;
    this.disclosureEl = this.root.querySelector(".disclosure")!;
    this.poweredEl = this.root.querySelector(".powered")!;
    this.closeBtn = this.root.querySelector(".close")!;
    this.newConvBtn = this.root.querySelector(".new-conv")!;
    this.humanBanner = this.root.querySelector(".human-banner")!;
    this.messagesEl = this.root.querySelector(".messages")!;
    this.handoffCtaEl = this.root.querySelector(".handoff-cta")!;
    this.handoffAffordanceBtn = this.handoffCtaEl.querySelector("button")!;
    this.form = this.root.querySelector("form.composer")!;
    this.input = this.form.querySelector("input")!;
    this.sendBtn = this.form.querySelector("button[type=submit]")!;
    this.applyDisclosure();
    this.applyIcons();

    this.launcher.addEventListener("click", () => {
      // The icon crosses out when the panel is open, so the button has to
      // act on that promise and close it.
      if (this.hasAttribute("open")) this.closePanel();
      else this.openPanel();
    });
    this.closeBtn.addEventListener("click", () => this.closePanel());
    this.newConvBtn.addEventListener("click", () => this.startNewConversation());
    this.handoffAffordanceBtn.addEventListener("click", () => void this.requestHandover());
    // Tapping the dimmed strip of page closes the sheet. The scrim only
    // exists at phone widths (CSS), so this listener is inert elsewhere.
    this.root.querySelector(".scrim")?.addEventListener("click", () => this.closePanel());
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.send();
    });
  }

  // Eagerly load branding once the element joins the DOM so the
  // launcher button paints in the tenant's primary colour from the
  // start instead of flashing the neutral default blue until the
  // user clicks. Cheap: one network round-trip on page load, then
  // cached for the lifetime of the element. The first openPanel()
  // hit re-uses the same branding promise.
  //
  // Deferred one microtask so synchronous post-mount test setup
  // (el.fetcher = stub, el.demoFetcher = stub) lands BEFORE the
  // fetch fires. In production this adds a single microsecond.
  connectedCallback(): void {
    queueMicrotask(() => void this.loadBranding());
    // Fix Round 1 minor: a RE-attach (a host SPA that moves the widget
    // in the DOM, or a framework that unmounts/remounts on route
    // change) would otherwise never reopen its stream — loadBranding()
    // short-circuits once brandingLoaded is already true and never
    // reaches its own ensureStream() call, and disconnectedCallback()
    // already closed the old one. Idempotent, and a harmless no-op on
    // the very first mount (no ids exist yet).
    this.ensureStream();
  }

  // Tears down the visitor stream when the element leaves the DOM, so a
  // removed/replaced widget doesn't keep a socket (or a pending
  // reconnect timer) alive with nothing left to render into. Fix Round
  // 1 (Minor 1): the wait-timeout timer is the one thing every OTHER
  // exit from "waiting" already clears (applyHumanState's else branch) —
  // without this, a detached element could still fire it up to
  // WAIT_TIMEOUT_MS later, holding the whole element alive for nothing.
  disconnectedCallback(): void {
    this.streamClose?.();
    this.streamClose = null;
    this.clearWaitTimeoutTimer();
  }

  // --- public API ---

  /** Programmatic open. Same as setting the `open` attribute. */
  openPanel(): void {
    this.setAttribute("open", "");
    this.applyLauncherLabel();
    void this.loadBranding();
    this.input.focus();
  }

  closePanel(): void {
    this.removeAttribute("open");
    this.applyLauncherLabel();
  }

  get tenant(): string {
    return this.getAttribute("tenant") ?? "";
  }

  get apiBase(): string {
    return this.getAttribute("api-base") ?? "https://api.chatmerce.eu";
  }

  // widgetKey is the per-widget public embed token from the snippet's
  // widget="..." attribute. When present, sendPublicChat forwards it
  // and the API can enforce a per-widget Origin allowlist; when
  // empty, the chat handler falls through to tenant.embed_origins.
  get widgetKey(): string {
    return this.getAttribute("widget") ?? "";
  }

  attributeChangedCallback(name: string): void {
    if (name === "tenant" && this.brandingLoaded) {
      // Tenant attribute changed after first load — invalidate so the
      // next open re-fetches. Rare in practice (embed pages don't
      // usually rotate tenants) but cheap to handle.
      this.brandingLoaded = false;
      this.brandingPromise = null;
      this.branding = null;
      this.welcomeText = "";
      this.welcomeButtons = [];
      // Fix Round 1 minor: every id below is scoped to the OLD tenant —
      // the storage keys are literally chatmerce-widget-*-${tenant} —
      // so left in place the widget would keep streaming the previous
      // tenant's conversation under the new tenant's chrome, and the
      // next send() would present the old tenant's conversation+token
      // pair to the NEW tenant's /chat endpoint. Reset the same way
      // startNewConversation() does (transcript included: those
      // messages belong to a different tenant's thread entirely), minus
      // its localStorage writes — those keys belong to the tenant that
      // is leaving, not the one arriving, and nothing here should touch
      // them. greet() runs again naturally next time loadBranding() /
      // applyBranding() completes for the new tenant.
      this.streamClose?.();
      this.streamClose = null;
      this.streamConvID = "";
      this.conversationID = "";
      this.clientToken = "";
      this.sessionID = "";
      this.applyHumanState("");
      this.messages = [];
      while (this.messagesEl.firstChild) this.messagesEl.removeChild(this.messagesEl.firstChild);
      this.suggestionsEl?.remove();
      this.suggestionsEl = null;
      this.handoffChipsEl = null;
      // The old tenant's message ids mean nothing under the new one.
      this.renderedMessageIdOrder = [];
      this.renderedMessageIds.clear();
      // Fix Round 2, Item 4: nothing else re-fetches until the panel is
      // next opened (openPanel() calls loadBranding() too, but a
      // visitor with the panel ALREADY open when the tenant switches
      // would otherwise stare at a blank panel — no greeting, no
      // branding — until they close and reopen it.
      if (this.hasAttribute("open")) void this.loadBranding();
    }
    if (name === "icon") this.applyIcons();
    if (name === "open") this.applyLauncherLabel();
    this.applyBrandingTitle();
    this.applyDisclosure();
    this.applyPoweredBy();
  }

  // --- internal ---

  // glyphFor builds the tenant's glyph as a fresh node tagged with the
  // class its slot needs. The `icon` attribute picks it: "mark" draws the
  // Chatmerce logo, an https URL loads the tenant's own, and anything
  // else — including no attribute at all — falls back to the neutral
  // bubble every tenant can live with.
  private glyphFor(cls: string): Element {
    const raw = (this.getAttribute("icon") ?? "").trim();
    if (/^https:\/\//i.test(raw)) {
      // https only, and only ever as an <img> src — built through the DOM
      // rather than interpolated into markup, so a hostile value cannot
      // smuggle in a script tag or an inline handler.
      const img = document.createElement("img");
      img.className = cls;
      img.src = raw;
      img.alt = "";
      return img;
    }
    const tpl = document.createElement("template");
    tpl.innerHTML = (raw === "mark" ? ICON_MARK : ICON_BUBBLE).trim();
    const node = tpl.content.firstElementChild as Element;
    node.classList.add(cls);
    return node;
  }

  // applyIcons paints every slot that shows the tenant's glyph: the
  // launcher, and the agent's avatar in the panel header. One attribute
  // drives both so they can never disagree.
  private applyIcons(): void {
    const launcher = this.root.querySelector(".launcher .icon-open");
    if (launcher) launcher.replaceWith(this.glyphFor("icon-open"));
    const head = this.root.querySelector(".head-glyph");
    if (head) head.replaceWith(this.glyphFor("head-glyph"));
  }

  // agentTurn pairs a bot node with the agent's avatar. The avatar is
  // what attributes the text, which is why the reply itself carries no
  // bubble.
  private agentTurn(node: HTMLElement): HTMLElement {
    const turn = document.createElement("div");
    turn.className = "turn";
    const avatar = document.createElement("span");
    avatar.className = "turn-avatar";
    avatar.setAttribute("aria-hidden", "true");
    avatar.appendChild(this.glyphFor("turn-glyph"));
    turn.append(avatar, node);
    return turn;
  }

  private applyLauncherLabel(): void {
    this.launcher.setAttribute(
      "aria-label",
      this.hasAttribute("open") ? "Close chat" : "Open chat",
    );
  }

  private async loadBranding(): Promise<void> {
    if (this.brandingLoaded || this.brandingPromise) {
      await this.brandingPromise;
      return;
    }
    const slug = this.tenant.trim();
    if (!slug) return;
    // Fire /demo and /widgets in parallel — both are lightweight, cached
    // at the backend, and neither is on the user's critical path (the
    // widget launcher renders immediately from CSS defaults).
    //
    // /demo returns branding + agent metadata + suggestion chips.
    // /widgets returns the welcome text for the tenant's default widget.
    // Fall back to /branding if /demo isn't available (older API).
    this.brandingPromise = (async (): Promise<PublicBranding | null> => {
      const [demoResult, widgetsResult] = await Promise.allSettled([
        this.demoFetcher(this.apiBase, slug),
        this.widgetsFetcher(this.apiBase, slug),
      ]);

      // Consume /widgets: pick the default widget (or first) and store welcome.
      if (widgetsResult.status === "fulfilled" && widgetsResult.value) {
        const wl = widgetsResult.value;
        const defaultID = wl.default_widget_id ?? "";
        const selected = (defaultID && wl.widgets.find((w) => w.id === defaultID)) || wl.widgets[0];
        if (selected?.welcome?.trim()) {
          this.welcomeText = selected.welcome.trim();
        }
        if (selected?.welcome_buttons?.length) {
          this.welcomeButtons = selected.welcome_buttons
            .filter((b) => b.label.trim() && b.message.trim())
            .map((b) => ({ label: b.label.trim(), message: b.message.trim() }));
        }
      }

      // Consume /demo: branding + agent name + suggestion chips.
      if (demoResult.status === "fulfilled" && demoResult.value) {
        const d = demoResult.value;
        this.demo = d;
        this.agentName = d.agent?.name ?? this.agentName;
        this.suggestions = d.agent?.suggestions ?? [];
        return d.branding;
      }

      // Fall back to the /branding-only endpoint (older API).
      return this.fetcher(this.apiBase, slug).catch(() => null);
    })();
    this.branding = await this.brandingPromise;
    this.brandingLoaded = true;
    this.ensureSession();
    this.applyBranding();
    // Independent of branding: a returning visitor whose conversation
    // (and token) survived in localStorage must start receiving a
    // possible staff reply on load, not only after they type again —
    // see ensureStream()'s own doc comment for why.
    this.ensureStream();
  }

  // store writes one widget key to localStorage and refreshes this
  // tenant's TTL marker, so an actively-used conversation never expires
  // while an abandoned one on a shared/kiosk device does (spec §6).
  private store(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
      window.localStorage.setItem(touchedKeyFor(this.tenant), String(Date.now()));
    } catch {
      /* localStorage unavailable (privacy mode etc.) — the widget still
         works for the lifetime of this instance, it just won't persist. */
    }
  }

  // expireStaleStorage clears every widget key for this tenant once the
  // TTL marker is older than STORAGE_TTL_MS. This is the client-side
  // half of closing the shared-device exposure that localStorage
  // persistence plus history-on-reload opens up (spec §6); the other
  // half is startNewConversation()'s visible affordance.
  private expireStaleStorage(): void {
    const tenant = this.tenant;
    try {
      const raw = window.localStorage.getItem(touchedKeyFor(tenant));
      if (!raw) return;
      const touchedAt = Number(raw);
      if (!Number.isFinite(touchedAt) || Date.now() - touchedAt <= STORAGE_TTL_MS) return;
      window.localStorage.removeItem(sessionKeyFor(tenant));
      window.localStorage.removeItem(conversationKeyFor(tenant));
      window.localStorage.removeItem(tokenKeyFor(tenant));
      window.localStorage.removeItem(touchedKeyFor(tenant));
    } catch {
      /* ignore */
    }
  }

  private ensureSession(): void {
    const tenant = this.tenant;
    const sKey = sessionKeyFor(tenant);
    const cKey = conversationKeyFor(tenant);
    const tKey = tokenKeyFor(tenant);

    this.expireStaleStorage();

    if (!this.sessionID) {
      try {
        const s = window.localStorage.getItem(sKey);
        if (s) this.sessionID = s;
      } catch {
        /* ignore */
      }
    }
    if (!this.sessionID) {
      // One-time migration from the pre-localStorage sessionStorage key,
      // so a visitor mid-conversation when this bundle lands keeps their
      // thread instead of silently starting a new one (spec §6). Copied
      // verbatim — this.store() also stamps the TTL marker, which a
      // migrated legacy value never had.
      try {
        const legacy = window.sessionStorage.getItem(sKey);
        if (legacy) {
          this.sessionID = legacy;
          this.store(sKey, legacy);
        }
      } catch {
        /* ignore */
      }
    }
    if (!this.sessionID) {
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);
      this.sessionID = "s_" + Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
      this.store(sKey, this.sessionID);
    }

    if (!this.conversationID) {
      try {
        this.conversationID = window.localStorage.getItem(cKey) ?? "";
      } catch {
        /* ignore */
      }
    }
    if (!this.conversationID) {
      try {
        const legacy = window.sessionStorage.getItem(cKey) ?? "";
        if (legacy) {
          this.conversationID = legacy;
          this.store(cKey, legacy);
        }
      } catch {
        /* ignore */
      }
    }

    if (!this.clientToken) {
      try {
        this.clientToken = window.localStorage.getItem(tKey) ?? "";
      } catch {
        /* ignore */
      }
    }
  }

  // ensureStream opens the visitor's live channel to a support agent's
  // reply as soon as BOTH a conversation and its client token exist —
  // NEVER gated on a response carrying human_state. This is the
  // milestone's acceptance criterion: an idle visitor who already
  // received their last bot reply will not call send() again, so if the
  // stream only opened when a /chat response carried human_state, a
  // staff reply would sit in Firestore unseen. Connect once a
  // conversation exists and let the server decide whether anything is
  // ever pushed (spec §7).
  //
  // Idempotent per conversation id: safe to call from both loadBranding
  // (page load / reopen) and send() (right after a turn mints the
  // conversation) without ever opening two sockets for the same thread.
  private ensureStream(): void {
    const convID = this.conversationID;
    const token = this.clientToken;
    if (!convID || !token) return;
    if (this.streamConvID === convID && this.streamClose) return;
    this.streamClose?.();
    this.streamConvID = convID;
    this.streamClose = this.streamOpener(
      this.apiBase,
      this.tenant.trim(),
      convID,
      token,
      (m) => this.onStreamMessage(m),
      (s) => this.onStreamState(s),
      { widgetKey: this.widgetKey, onTokenInvalid: () => this.discardConversationIdentity() },
    );
  }

  // markMessageRendered records id and reports whether this is the
  // first time it's been seen — see the renderedMessageIds field
  // comment above for why this dedupe has to live on the component,
  // not inside one stream instance.
  private markMessageRendered(id: string): boolean {
    if (!id) return true; // no real message lacks one; never suppress on this basis
    if (this.renderedMessageIds.has(id)) return false;
    this.renderedMessageIds.add(id);
    this.renderedMessageIdOrder.push(id);
    if (this.renderedMessageIdOrder.length > ChatmerceChat.RENDERED_ID_MAX) {
      const oldest = this.renderedMessageIdOrder.shift();
      if (oldest !== undefined) this.renderedMessageIds.delete(oldest);
    }
    return true;
  }

  // onStreamMessage renders a message the stream delivered (from either
  // the socket or its REST catch-up — stream.ts already deduped by id
  // WITHIN one stream instance; markMessageRendered above covers a
  // remount's brand-new instance), but ONLY roles "human" (Fix Round 1,
  // Item 2) and "system" (M1b: the server's own canned closing line —
  // forwardToVisitor, public_stream.go). A catch-up can also return
  // "user"/"bot" rows (spec §7: the REST endpoint carries every role,
  // unlike the socket), and those stay ignored here on purpose: this
  // widget does not attempt to rebuild the transcript from them.
  // loadBranding() runs greet() — which appends the welcome bubble —
  // BEFORE ensureStream() ever fires, so `this.messages` is never
  // actually empty when a catch-up lands, and gating on that length was
  // dead code that (per its own dead branch) would ALSO have rendered a
  // bare user turn with no bot reply above it whenever branding failed
  // to load. Today's widget already renders no history on load; keeping
  // that here avoids both the orphan bubble and the shared-device
  // transcript-on-reload exposure spec §6 flags (server-side idle
  // expiry is deferred to M1b, so there is nothing yet to bound how old
  // a rebuilt transcript could be).
  //
  // "bot" is deliberately NOT added alongside "system": persistDemoTurn's
  // bot replies carry no id in the /chat response that already rendered
  // them, so the widget has nothing to dedupe against and every ordinary
  // reply would render a second time. "system" is safe precisely because
  // it is the one role the server ever originates itself with no prior
  // echo to this widget — currently only the ticket-resolve closing line.
  private onStreamMessage(m: StreamMessage): void {
    if (m.role !== "human" && m.role !== "system") return;
    // Case 9: a human message is itself proof someone is now engaged,
    // independent of whether (or when) a "state" frame confirming
    // "active" follows it — the timer must not survive to fire against
    // a conversation a person has already answered. Cancelled
    // unconditionally (not just when humanState === "waiting"): this is
    // a no-op when no timer is pending, and there is no route by which
    // a "human" message and a live wait-timeout offer coexist that this
    // would break.
    if (m.role === "human") this.clearWaitTimeoutTimer();
    if (!this.markMessageRendered(m.id)) return;
    if (m.role === "system") {
      // Rendered as an ordinary agent bubble — no author label, unlike a
      // human reply, since this is Chatmerce's own courtesy line, not a
      // named person.
      this.appendMessage({ role: "agent", text: m.text });
      return;
    }
    this.appendMessage({ role: "human", text: m.text, author: m.authorName });
  }

  private onStreamState(s: StreamStateUpdate): void {
    this.applyHumanState(s.humanState);
  }

  // discardConversationIdentity drops everything that ties the widget to
  // the CURRENT conversation — the stream, the ids, the persisted conv +
  // token keys, and the human-state banner that described it — without
  // touching sessionID or the on-screen transcript. Shared by every path
  // that discovers the conversation+token pair is dead: the stream's
  // exact "invalid client token" refusal, and (Fix Round 1, Item 1) the
  // same refusal from /chat itself. sessionID is deliberately kept here:
  // it is still a valid rate-limit identity, and the next /chat call
  // mints a brand-new conversation + token under it — this is recovery
  // from a broken pair, not the visitor asking to start over (that's
  // startNewConversation(), which also clears sessionID and the
  // transcript).
  private discardConversationIdentity(): void {
    this.streamClose?.();
    this.streamClose = null;
    this.streamConvID = "";
    this.conversationID = "";
    this.clientToken = "";
    this.applyHumanState("");
    try {
      window.localStorage.removeItem(conversationKeyFor(this.tenant));
      window.localStorage.removeItem(tokenKeyFor(this.tenant));
    } catch {
      /* ignore */
    }
  }

  // applyHumanState toggles the "a person is helping you" banner. Fed
  // both by /chat responses (send()) and the stream (onStreamState) —
  // whichever notices a change first wins, and the other's repeat is a
  // no-op.
  private applyHumanState(state: string): void {
    if (state === this.humanState) return;
    this.humanState = state;
    if (this.humanBanner) {
      if (state === "active" || state === "waiting" || state === "pending") {
        this.humanBanner.hidden = false;
        this.humanBanner.textContent = humanBannerText(this.resolveWidgetLocale(), state);
      } else {
        this.humanBanner.hidden = true;
      }
    }
    // The persistent handoff affordance hides for the same three states
    // that show the banner, and reappears the instant they clear (Fix
    // Round 1 product ruling).
    this.applyHandoffAffordance();
    // Task 16, case 1/6/10/11: the contact form and the wait-timeout are
    // scoped to EXACTLY the "waiting" state. The guard above already
    // guarantees state !== the previous humanState, so reaching this
    // branch is always a fresh entry into (or departure from) waiting —
    // never a repeat. Entering arms the timer and renders the form;
    // leaving by ANY route (a resolved /mode change, a stream state
    // update, discardConversationIdentity()'s reset) clears both, so
    // neither can ever act on a conversation that has moved on. "active"
    // (case 10) and every other state take the else branch, so the
    // offer is never even armed there, not merely hidden after the
    // fact.
    if (state === "waiting") {
      this.renderContactForm();
      this.armWaitTimeout();
    } else {
      this.clearWaitTimeoutState();
    }
  }

  // armWaitTimeout (re)schedules the wait-timeout offer. Called once on
  // entering "waiting" (applyHumanState) and again each time the visitor
  // taps "Keep waiting" (case 8) — never anywhere else, so the offer can
  // fire at most once per arming and is always superseded by a fresh
  // arm rather than stacking a second pending timer alongside it.
  private armWaitTimeout(): void {
    this.clearWaitTimeoutTimer();
    this.waitTimeoutTimer = this.setTimeoutImpl(() => {
      this.waitTimeoutTimer = null;
      this.showWaitTimeoutOffer();
    }, WAIT_TIMEOUT_MS);
  }

  private clearWaitTimeoutTimer(): void {
    if (this.waitTimeoutTimer) {
      this.clearTimeoutImpl(this.waitTimeoutTimer);
      this.waitTimeoutTimer = null;
    }
  }

  // clearWaitTimeoutState is the single place that tears down every
  // trace of the current waiting episode — the pending timer (case 11),
  // the rendered offer (in case it already fired), and the contact form
  // — plus the contact_prompt text that belonged to it, so a LATER
  // waiting episode never inherits it (see contactPromptText's own
  // field comment).
  private clearWaitTimeoutState(): void {
    this.clearWaitTimeoutTimer();
    this.waitTimeoutOfferEl?.remove();
    this.waitTimeoutOfferEl = null;
    this.contactFormEl?.remove();
    this.contactFormEl = null;
    this.contactPromptText = "";
  }

  // showWaitTimeoutOffer renders the "still waiting?" offer (case 6).
  // Guarded on humanState even though every path that could fire this
  // timer late already cleared it (case 9/11) — belt-and-braces, the
  // same convention this file already uses for chip clicks that survive
  // past their state (e.g. requestHandover's guards), so a future change
  // to when the timer is cleared fails safe instead of silently
  // offering a choice that no longer makes sense.
  private showWaitTimeoutOffer(): void {
    if (this.humanState !== "waiting") return;
    this.waitTimeoutOfferEl?.remove();
    const locale = this.resolveWidgetLocale();
    const copy = waitTimeoutCopy(locale);
    const wrap = document.createElement("div");
    wrap.className = "wait-timeout-offer";
    const message = document.createElement("p");
    message.className = "wait-timeout-message";
    message.textContent = copy.message;
    wrap.appendChild(message);
    const row = document.createElement("div");
    row.className = "chips";
    const backBtn = document.createElement("button");
    backBtn.type = "button";
    backBtn.className = "chip";
    backBtn.textContent = copy.backToAssistant;
    // Case 7: this is the WHOLE POINT of the timeout — before /mode
    // existed, a visitor who chose to wait had no way back to the
    // assistant at all. Routed through chooseHandoffMode() (not a
    // one-off inline call) so it gets the same busy guard, the same
    // ERR_INVALID_CLIENT_TOKEN/ERR_HANDOFF_ACTIVE handling, and the
    // same "clears the banner on success" behaviour as the wait/continue
    // chips already have.
    backBtn.addEventListener("click", () => void this.chooseHandoffMode("bot_ok"));
    const keepBtn = document.createElement("button");
    keepBtn.type = "button";
    keepBtn.className = "chip";
    keepBtn.textContent = copy.keepWaiting;
    // Case 8: dismiss-and-re-arm is purely local — no server round
    // trip. It DOES still go through setBusy()'s blanket disable like
    // every other `.chip` (Fix Round 1, Minor 2 — an earlier version of
    // this comment claimed otherwise, which would mislead a reader
    // checking whether this needs its own busy guard: it doesn't,
    // precisely because setBusy() already covers it for free).
    keepBtn.addEventListener("click", () => {
      wrap.remove();
      if (this.waitTimeoutOfferEl === wrap) this.waitTimeoutOfferEl = null;
      this.armWaitTimeout();
    });
    row.append(backBtn, keepBtn);
    wrap.appendChild(row);
    this.messagesEl.appendChild(wrap);
    this.waitTimeoutOfferEl = wrap;
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // renderContactForm renders the contact-capture form (case 1-4):
  // prompt text (the server's own contact_prompt when this waiting
  // episode has one, else a localized default), an email field, submit
  // and skip. Both submit and skip carry class="chip" so setBusy()'s
  // existing blanket disable already covers them — no new plumbing
  // needed there (see setBusy()'s own doc comment on why every
  // interactive control routes through it).
  private renderContactForm(): void {
    this.contactFormEl?.remove();
    this.contactFormEl = null;
    const locale = this.resolveWidgetLocale();
    const copy = contactFormCopy(locale);
    const wrap = document.createElement("div");
    wrap.className = "contact-form";

    const prompt = document.createElement("p");
    prompt.className = "contact-form-prompt";
    prompt.textContent = this.contactPromptText || copy.prompt;
    wrap.appendChild(prompt);

    const row = document.createElement("div");
    row.className = "contact-form-row";
    const input = document.createElement("input");
    input.type = "email";
    input.placeholder = copy.placeholder;
    input.setAttribute("aria-label", copy.placeholder);
    row.appendChild(input);

    const submitBtn = document.createElement("button");
    submitBtn.type = "button";
    submitBtn.className = "chip contact-submit";
    submitBtn.textContent = copy.submit;
    row.appendChild(submitBtn);
    wrap.appendChild(row);

    const error = document.createElement("p");
    error.className = "contact-form-error";
    error.hidden = true;
    wrap.appendChild(error);

    const skipBtn = document.createElement("button");
    skipBtn.type = "button";
    skipBtn.className = "chip contact-skip";
    skipBtn.textContent = copy.skip;
    wrap.appendChild(skipBtn);

    submitBtn.addEventListener("click", () => void this.submitContactEmail(input, error, wrap));
    // Case 4: skipping is a first-class path, not an escape hatch —
    // nothing is gated behind having provided an address. The form is
    // simply removed; the banner and the composer are untouched, and
    // the visitor is still reachable over the socket regardless.
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter") void this.submitContactEmail(input, error, wrap);
    });
    skipBtn.addEventListener("click", () => {
      if (this.busy) return;
      wrap.remove();
      if (this.contactFormEl === wrap) this.contactFormEl = null;
    });

    this.messagesEl.appendChild(wrap);
    this.contactFormEl = wrap;
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // collapseContactForm replaces the live form with a standalone note
  // appended to .messages directly — deliberately NOT inside `wrap`
  // (and never tracked as contactFormEl again) so a LATER
  // clearWaitTimeoutState() call (leaving "waiting" by any route: the
  // "go back" button, a staff takeover, a stream state update) cannot
  // erase it (Fix Round 1, Minor 4 — an earlier version reused `wrap`
  // itself for the confirmation, which is exactly the node
  // clearWaitTimeoutState() removes).
  private collapseContactForm(wrap: HTMLElement, className: string, text: string): void {
    wrap.remove();
    if (this.contactFormEl === wrap) this.contactFormEl = null;
    const note = document.createElement("p");
    note.className = className;
    note.textContent = text;
    this.messagesEl.appendChild(note);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // submitContactEmail is the form's submit target (case 2/3/5). An
  // invalid address never reaches contactSender at all (case 3) — the
  // shape check runs before setBusy(true), so a rejected submission
  // never disables the form the visitor still needs to correct it.
  // Fix Round 1, Important 2: the length check that used to live only
  // inside sendPublicContact now runs here too, in the SAME branch as
  // the shape regex — an oversized value must read as "fix the
  // address", not round-trip to the sender's own guard and come back
  // as a generic transport error for something that can never succeed.
  private async submitContactEmail(
    input: HTMLInputElement,
    errorEl: HTMLElement,
    wrap: HTMLElement,
  ): Promise<void> {
    if (this.busy) return;
    const locale = this.resolveWidgetLocale();
    const copy = contactFormCopy(locale);
    const email = input.value.trim();
    if (!CONTACT_EMAIL_RE.test(email) || email.length > MAX_CONTACT_EMAIL_LEN) {
      errorEl.textContent = copy.invalidEmail;
      errorEl.hidden = false;
      return;
    }
    errorEl.hidden = true;
    this.setBusy(true);
    try {
      await this.contactSender(
        this.apiBase,
        this.tenant.trim(),
        this.conversationID,
        this.clientToken,
        email,
      );
      // Case 2: collapse to a confirmation rather than removing the
      // form outright — the visitor's own submission stays visible as
      // an acknowledgement, the same way chooseHandoffMode's success
      // path leaves the banner up rather than clearing everything.
      this.collapseContactForm(wrap, "contact-form-confirmation", copy.confirmation);
    } catch (e) {
      if (e instanceof Error && e.message === ERR_INVALID_CLIENT_TOKEN) {
        this.discardConversationIdentity();
        return;
      }
      // Fix Round 1, Important 2: the server rejected the ADDRESS ITSELF
      // (400) — the visitor needs the same "fix the address" copy the
      // client-side check above shows, not a generic "try again": the
      // server's mail.ParseAddress is stricter than this widget's
      // permissive regex on purpose (see sendPublicContact's own doc
      // comment — duplicating the server's validator here would make the
      // widget the stricter gate), so this branch IS reachable even
      // though the regex passed.
      if (e instanceof Error && e.message === ERR_CONTACT_INVALID_EMAIL) {
        errorEl.textContent = copy.invalidEmail;
        errorEl.hidden = false;
        return;
      }
      // Fix Round 1, Important 2: no active ticket (404) or one that's
      // already closed (409) — a retry is structurally impossible,
      // unlike a 429/5xx. Collapse rather than inviting one.
      if (e instanceof Error && e.message === ERR_CONTACT_CLOSED) {
        this.collapseContactForm(wrap, "contact-form-closed", copy.closed);
        return;
      }
      // Case 5: a 429 (or any other transport failure) must not read as
      // a hard error — transportErrorText already draws exactly this
      // distinction for send()/requestHandover()/chooseHandoffMode(), so
      // reuse it verbatim rather than inventing a second copy of the
      // same judgment call. The form is left in place (not collapsed),
      // so the visitor can simply try again.
      const msg = e instanceof Error ? e.message : String(e);
      errorEl.textContent = transportErrorText(locale, msg === "rate_limited");
      errorEl.hidden = false;
    } finally {
      this.setBusy(false);
    }
  }

  // startNewConversation is the visible half of closing the
  // shared-device exposure localStorage persistence opens up (spec
  // §6): a visitor (or the next person at a kiosk) can always discard
  // the current thread rather than wait out the TTL.
  private startNewConversation(): void {
    this.discardConversationIdentity();
    // Fix Round 1, Item 4: the server's newConvID embeds the session id
    // (public_demo.go:499-503), so a "fresh" thread that keeps the same
    // sessionID is trivially linkable back to the previous visitor in
    // the inbox — exactly what this button exists to prevent. The
    // rate-limit-identity rationale for keeping sessionID applies to the
    // invalid-token RECOVERY paths in discardConversationIdentity(), not
    // to the one control whose entire purpose is discarding the
    // previous person's identity — so this clears it too, and the
    // legacy sessionStorage copies as well (a same-tab visitor who went
    // through the sessionStorage→localStorage migration earlier in this
    // session would otherwise resurrect the old session id here).
    this.sessionID = "";
    this.messages = [];
    while (this.messagesEl.firstChild) this.messagesEl.removeChild(this.messagesEl.firstChild);
    this.suggestionsEl?.remove();
    this.suggestionsEl = null;
    this.handoffChipsEl = null;
    // The transcript is gone, so the dedupe record of what's on screen
    // must go with it — otherwise a message id from a NEW conversation
    // that happened to collide would be silently suppressed forever.
    this.renderedMessageIdOrder = [];
    this.renderedMessageIds.clear();
    try {
      window.localStorage.removeItem(sessionKeyFor(this.tenant));
      window.localStorage.removeItem(touchedKeyFor(this.tenant));
      window.sessionStorage.removeItem(sessionKeyFor(this.tenant));
      window.sessionStorage.removeItem(conversationKeyFor(this.tenant));
    } catch {
      /* ignore */
    }
    // Mint the new identity now rather than waiting for the next send(),
    // and restore the greeting + quick replies the transcript wipe above
    // just removed — greet()'s own guard (messages.length === 0) is what
    // makes this safe to call unconditionally.
    this.ensureSession();
    this.greet();
  }

  private applyBranding(): void {
    if (!this.branding) {
      this.applyBrandingTitle();
      this.applyPoweredBy();
      return;
    }
    const sanitised = sanitiseBrandVars(this.branding.demo_css_vars);
    // Allow the top-level primary_color / accent fields to set vars too,
    // for tenants that haven't gone through the AI styling generator.
    if (this.branding.primary_color && /^#[0-9a-fA-F]{3,6}$/.test(this.branding.primary_color)) {
      sanitised["--brand-primary"] = this.branding.primary_color.toLowerCase();
    }
    if (this.branding.accent && /^#[0-9a-fA-F]{3,6}$/.test(this.branding.accent)) {
      sanitised["--brand-accent"] = this.branding.accent.toLowerCase();
    }
    for (const [k, v] of Object.entries(sanitised)) {
      this.style.setProperty(k, v);
    }
    this.applyBrandingTitle();
    this.applyDisclosure();
    this.applyPoweredBy();
    this.greet();
  }

  private applyBrandingTitle(): void {
    if (!this.title) return;
    this.title.textContent = this.branding?.name ?? "Chat";
  }

  // resolveWidgetLocale derives the widget's display locale for the
  // AI-disclosure line: explicit lang attr > tenant widget_locale
  // (concrete, or "auto" ⇒ host <html lang> > browser) > pl fallback.
  // <html lang>/navigator are only consulted when the tenant opted into
  // "auto" — see ./disclosure.ts for the full resolution rules.
  private resolveWidgetLocale(): WidgetLocale {
    const attr = this.getAttribute("lang");
    const tenant = this.branding?.widget_locale ?? null;
    const htmlLang =
      typeof document !== "undefined" ? document.documentElement.getAttribute("lang") : null;
    const navLang = typeof navigator !== "undefined" ? navigator.language : null;
    return resolveLocale({ attr, tenant, htmlLang, navLang });
  }

  // applyDisclosure renders the always-on AI-disclosure line (EU AI Act
  // Art. 50 transparency). It never depends on the branding fetch
  // succeeding — the localized default renders immediately, and a
  // pro/enterprise tenant's override (branding.ai_disclosure) replaces
  // it once branding resolves.
  private applyDisclosure(): void {
    this.input.placeholder = composerPlaceholder(this.resolveWidgetLocale());
    if (this.newConvBtn) {
      const label = NEW_CONVERSATION_LABEL[this.resolveWidgetLocale()] ?? NEW_CONVERSATION_LABEL.pl;
      this.newConvBtn.setAttribute("aria-label", label);
      this.newConvBtn.title = label;
    }
    // Locale changes (and this method's own call from applyBranding(),
    // once support_handoff_enabled is first known) both need the
    // persistent handoff affordance's label — and, on that first call,
    // its visibility — refreshed the same way the new-conversation
    // label above is.
    this.applyHandoffAffordance();
    if (!this.disclosureEl) return;
    this.disclosureEl.textContent = disclosureText(
      this.resolveWidgetLocale(),
      this.branding?.ai_disclosure ?? null,
    );
  }

  // applyPoweredBy renders the tier-gated "powered by chatmerce.eu"
  // footer. Only called from applyBranding() (never in the constructor
  // path) so it never flashes on for a paid tier before branding
  // resolves — before that point the footer stays empty. Built with
  // createElement/textContent (no innerHTML) since the link text is a
  // static literal, not tenant content.
  private applyPoweredBy(): void {
    if (!this.poweredEl) return;
    while (this.poweredEl.firstChild) {
      this.poweredEl.removeChild(this.poweredEl.firstChild);
    }
    if (this.branding?.show_powered_by !== true) return;
    const link = document.createElement("a");
    link.href = "https://chatmerce.eu";
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent = "powered by chatmerce.eu";
    this.poweredEl.appendChild(link);
  }

  private greet(): void {
    if (this.messages.length > 0 || !this.branding) return;
    // Prefer the per-widget welcome string configured by the tenant. It is
    // fetched from /widgets in loadBranding() alongside /demo. When present
    // it already contains the right copy (agent name, tone, language) so we
    // use it verbatim. Fall back to a generic greeting only when no widget
    // config exists yet (e.g. very early-stage tenants).
    const greeting =
      this.welcomeText ||
      fallbackGreeting(this.resolveWidgetLocale(), this.agentName, this.branding.name);
    this.appendMessage({ role: "agent", text: greeting });
    // Welcome quick-reply buttons take precedence on first open; if none are
    // configured, fall back to the agent's suggestion chips.
    if (this.welcomeButtons.length > 0) {
      this.renderWelcomeButtons();
    } else {
      this.renderSuggestions();
    }
  }

  // setBusy is the single place that flips `busy` — never assign
  // `this.busy` directly (see its own field comment). Fix Round 3,
  // IMPORTANT: before this existed, send()'s own guard disabled
  // sendBtn/input/handoffAffordanceBtn at each of its three call sites,
  // but nothing disabled a suggestion/welcome/rich-card chip — so a chip
  // click while `busy` was true (e.g. a warm-path /handover still
  // pending) hit send()'s guard and returned silently: no error, no
  // sent message, the chip's own text left sitting in the composer
  // looking like an abandoned draft. Centralising the toggle here, over
  // EVERY `.chip` currently in the shadow root plus the three named
  // controls, means a chip is either usable or visibly disabled —
  // never silently inert — and no future call site can add a fourth
  // control without also touching this one. Fix Round 1, Minor 3: the
  // contact form's email field joined the sweep too — it isn't a
  // `.chip`, but it is exactly the kind of interactive control this
  // method exists to cover (a visitor could otherwise edit the address
  // while its own submission is still in flight).
  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.sendBtn.disabled = busy;
    this.input.disabled = busy;
    this.handoffAffordanceBtn.disabled = busy;
    this.root.querySelectorAll<HTMLButtonElement>(".chip").forEach((btn) => {
      btn.disabled = busy;
    });
    this.root.querySelectorAll<HTMLInputElement>(".contact-form-row input").forEach((el) => {
      el.disabled = busy;
    });
  }

  // applyHandoffAffordance keeps the persistent "Talk to a human"
  // control (Fix Round 1 product ruling) in sync with two things that
  // can each change independently: whether the tenant has support turned
  // on at all, and whether a human is already involved in this specific
  // conversation. Visible iff support_handoff_enabled AND humanState is
  // empty — hidden the instant a ticket exists (pending/waiting/active),
  // restored the instant it clears (bot_ok back to no human involved, or
  // a brand-new conversation). Called from applyDisclosure() (locale/
  // branding changes) and applyHumanState() (state changes) — never
  // tied to greet()/the message list, unlike the old greeting-only
  // design: a visitor five turns into an unhelpful conversation must
  // still be able to find this, not just a first-time visitor who has
  // not been failed by the bot yet.
  private applyHandoffAffordance(): void {
    if (!this.handoffCtaEl || !this.handoffAffordanceBtn) return;
    const show = this.branding?.support_handoff_enabled === true && this.humanState === "";
    this.handoffCtaEl.hidden = !show;
    this.handoffAffordanceBtn.textContent = handoffAffordanceLabel(this.resolveWidgetLocale());
  }

  // sendTriggerPhrasePreservingDraft is requestHandover()'s cold-start
  // fallback (Fix Round 1 CRITICAL 1), factored out because TWO call
  // sites need it (the upfront "no conversation yet" check, and the
  // ERR_INVALID_CLIENT_TOKEN recovery below — the stored pair being
  // gone is the SAME state).
  //
  // Fix Round 2, Minor: overwriting this.input.value outright used to
  // silently destroy a draft the visitor had already typed but not yet
  // sent — probed losing "my order number is 12345". The visitor tapped
  // the AFFORDANCE, not send, so their own words are not an implicit
  // submission; chosen fix is to preserve the draft as still-editable
  // rather than also sending it (sending it as a second message would
  // either need it to happen BEFORE the trigger phrase — spending a
  // real LLM turn on unrelated content before the visitor's actual ask
  // even reaches a person — or risk the two getting interleaved with
  // whatever the visitor types next). The draft is stashed, the trigger
  // phrase is sent alone (so the zero-cost guarantee for THIS message
  // still holds), and the draft is restored once that completes — but
  // only if the composer is still empty, so a visitor who started typing
  // something NEW while the trigger phrase was in flight is never
  // overwritten by the OLD draft.
  private async sendTriggerPhrasePreservingDraft(): Promise<void> {
    const draft = this.input.value;
    this.input.value = handoffAffordanceLabel(this.resolveWidgetLocale());
    await this.send();
    if (!this.input.value) this.input.value = draft;
  }

  // requestHandover is the affordance's click target. When a conversation
  // already exists, it calls POST …/handover directly — never through
  // send()/chatSender, since routing a button press through /chat would
  // spend an LLM turn and the tenant's monthly quota on exactly what the
  // endpoint exists to avoid (case 2).
  //
  // Fix Round 1, CRITICAL 1: a FIRST-TIME visitor (or one whose stored
  // pair was just discarded) has no conversationID/clientToken yet —
  // /handover targets an existing conversation server-side and 404s/403s
  // on an empty or unknown one, which is not a recoverable state the way
  // ERR_INVALID_CLIENT_TOKEN is. There is no non-LLM endpoint to mint a
  // conversation ahead of time, so this falls back to the ordinary chat
  // path instead: sending HANDOFF_AFFORDANCE_LABEL's own text as if
  // typed. public_demo.go's keyword short-circuit (handoff.Requested)
  // matches that EXACT phrase (see the label's own doc comment) ahead of
  // both the quota gate and Turn, so this still costs neither — send()
  // already handles minting the conversation, storing the token, opening
  // the stream, applying human_state and rendering the inline `handoff`.
  //
  // Fix Round 2, IMPORTANT: this.busy is a re-entrancy guard SHARED with
  // send() (see its own field comment). Without it, an impatient visitor
  // — a real question submitted, then a tap on this control while it's
  // still in flight — hit conversationID still being "" mid-flight and
  // fired a SECOND concurrent /chat with an empty conversation id: two
  // conversations, two tokens, two tickets, and the visitor's real
  // question orphaned in the one no ticket points at. This is
  // deliberately NOT wrapped in a try/finally: every exit path calls
  // setBusy() explicitly. The cold-start branch never calls setBusy(true)
  // at all — it delegates straight to sendTriggerPhrasePreservingDraft()
  // (→ send()), which acquires the flag itself. The post-403 catch
  // branch DOES acquire it (the direct /handover attempt needs guarding
  // too), so THAT one releases it with setBusy(false) immediately before
  // delegating — a trailing finally here would otherwise clobber send()'s
  // own in-flight `true` back to false the instant this function
  // returns, defeating the guard for exactly the case it exists to cover.
  private async requestHandover(): Promise<void> {
    if (this.busy) return;
    if (!this.conversationID || !this.clientToken) {
      await this.sendTriggerPhrasePreservingDraft();
      return;
    }
    this.setBusy(true);
    try {
      const payload = await this.handoverSender(
        this.apiBase,
        this.tenant.trim(),
        this.conversationID,
        this.clientToken,
        this.resolveWidgetLocale(),
      );
      this.setBusy(false);
      this.renderHandoffMessage(payload);
    } catch (e) {
      if (e instanceof Error && e.message === ERR_INVALID_CLIENT_TOKEN) {
        // Case 9: a 403 from either endpoint takes the same recovery
        // path /chat itself already uses. The stored pair is now gone —
        // exactly the "no conversation" state above — so fall back the
        // same way rather than leaving the visitor stuck. Released
        // BEFORE delegating: send() (inside
        // sendTriggerPhrasePreservingDraft) re-acquires it itself.
        this.discardConversationIdentity();
        this.setBusy(false);
        await this.sendTriggerPhrasePreservingDraft();
        return;
      }
      this.setBusy(false);
      // Fix Round 1, Important 2: a transient failure (429/5xx/network)
      // must not cost the visitor the affordance itself. Unlike the old
      // greeting-only chip, this control is never removed before the
      // request — its visibility is driven solely by
      // applyHandoffAffordance(), which nothing here has changed — so
      // there is nothing to restore beyond leaving it exactly as it was.
      const msg = e instanceof Error ? e.message : String(e);
      this.appendMessage({
        role: "agent",
        text: transportErrorText(this.resolveWidgetLocale(), msg === "rate_limited"),
      });
    }
  }

  // renderHandoffMessage renders one holding-message turn: the payload's
  // text as an ordinary agent bubble, plus one chip per choice (wait
  // first — the server already orders them that way, composeHandoffPayload
  // in public_handoff.go). Called from BOTH the affordance's own
  // /handover response and a /chat response carrying `handoff` inline
  // (case 4) — same function, same rendering, because the visitor must
  // not be able to tell which path opened the ticket.
  //
  // choices[0] always means "wait" and choices[1] always means "continue"
  // — composeHandoffPayload's own ordering guarantee, not something this
  // widget infers from the label text (which is tenant-locale copy, not
  // a machine-readable tag). Sliced to at most 2 (Fix Round 1, Minor 2)
  // so a hypothetical third entry can never fall through to "bot_ok" by
  // default — composeHandoffPayload only ever emits 0 or 2, but the
  // widget should not silently rely on that never changing.
  private renderHandoffMessage(payload: HandoffPayload): void {
    // A stale chip row from an earlier holding message must not linger
    // under a new one (case 7).
    this.handoffChipsEl?.remove();
    this.handoffChipsEl = null;
    // The direct /handover response carries no human_state of its own
    // (unlike a /chat response, which already set it above in send()) —
    // infer it here so the persistent affordance hides and the banner
    // shows immediately, without waiting for the stream to catch up.
    // Only when humanState is still unknown ("""): composeHandoffPayload
    // bases Choices on ticket.Status alone, not CustomerMode, so a
    // SECOND handoff response must never downgrade an already-known
    // "waiting"/"active" back to "pending".
    if (this.humanState === "") {
      this.applyHumanState(payload.choices.length > 0 ? "pending" : "active");
    }

    this.appendMessage({ role: "agent", text: payload.text });
    const choices = payload.choices.slice(0, 2);
    if (choices.length === 0) return;
    const row = document.createElement("div");
    row.className = "chips";
    choices.forEach((label, i) => {
      const mode: HandoffMode = i === 0 ? "waiting" : "bot_ok";
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "chip";
      btn.textContent = label;
      btn.addEventListener("click", () => void this.chooseHandoffMode(mode));
      row.appendChild(btn);
    });
    this.messagesEl.appendChild(row);
    this.handoffChipsEl = row;
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // chooseHandoffMode is the wait/continue chip's click target: POST
  // …/mode directly (cases 5/6). The spec's §4 fallback — the server
  // classifying a visitor's TYPED answer as the same choice — stays
  // supported server-side; this is the reliable path a tap takes instead
  // of spending a turn on an ordinary chat message.
  // Fix Round 3: guarded with the same shared `busy` flag/setBusy() as
  // send()/requestHandover(), for consistency rather than necessity —
  // this path is structurally safe today regardless (its chip row is
  // removed synchronously, before any await, so a second physical click
  // has no button left to hit), but leaving it as the one uncovered
  // interactive path would only make the NEXT person re-derive that
  // same reasoning from scratch. A plain try/finally is safe here
  // (unlike requestHandover()'s cold-start branch): nothing delegates
  // out of this function into another setBusy()-guarded one.
  private async chooseHandoffMode(mode: HandoffMode): Promise<void> {
    if (this.busy) return;
    // Removed on tap, not just on success (case 7): holding the row open
    // through the round trip risks a double-tap racing the request, and
    // an in-flight choice is already answered from the visitor's side.
    // Same reasoning covers the wait-timeout offer (Task 16, case 7) —
    // this is now its "Go back to the assistant" button's own target too.
    this.handoffChipsEl?.remove();
    this.handoffChipsEl = null;
    this.waitTimeoutOfferEl?.remove();
    this.waitTimeoutOfferEl = null;
    // Task 16: a /mode response never carries contact_prompt (only a
    // /chat response does — see PublicChatResponse.contact_prompt's own
    // doc comment), so a mode:"waiting" call reaching "waiting" here
    // must not render a PRIOR episode's server-supplied prompt text —
    // reset unconditionally; applyHumanState()'s own render path fills
    // it back in immediately from resp.contact_prompt when send() sets
    // it, but this function never does.
    this.contactPromptText = "";
    this.setBusy(true);
    try {
      const resp = await this.modeSender(
        this.apiBase,
        this.tenant.trim(),
        this.conversationID,
        this.clientToken,
        mode,
      );
      this.applyHumanState((resp.human_state ?? "").trim());
    } catch (e) {
      if (e instanceof Error && e.message === ERR_INVALID_CLIENT_TOKEN) {
        this.discardConversationIdentity();
        return;
      }
      if (e instanceof Error && e.message === ERR_HANDOFF_ACTIVE) {
        // Case 8: a human already claimed the thread between the holding
        // message rendering and this tap (staff "Take over", or another
        // tab's own choice winning the race). Good news, not a failure —
        // surface the same "you're with a person now" banner the stream
        // uses, instead of an error toast.
        this.applyHumanState("active");
        return;
      }
      if (e instanceof Error && e.message === ERR_MODE_CLOSED) {
        // Fix Round 2 — the mirror of the CRITICAL fix below: the
        // ticket resolved, expired, or vanished concurrently (a 404, or
        // a 409 that ISN'T "ticket is active" — see ERR_MODE_CLOSED's
        // own doc comment). The conversation genuinely IS over, so
        // unlike the generic branch this must NOT re-arm — an offer
        // that leads nowhere is worse than none. Reuses /contact's own
        // "closed" copy rather than inventing a second string; clearing
        // to "" (not "active") tears the whole waiting UI down via
        // applyHumanState()'s own else branch and lets the persistent
        // "Talk to a human" affordance return, in case the visitor still
        // needs a person.
        const copy = contactFormCopy(this.resolveWidgetLocale());
        this.appendMessage({ role: "agent", text: copy.closed });
        this.applyHumanState("");
        return;
      }
      const msg = e instanceof Error ? e.message : String(e);
      this.appendMessage({
        role: "agent",
        text: transportErrorText(this.resolveWidgetLocale(), msg === "rate_limited"),
      });
      // Fix Round 1, CRITICAL: this is the ONLY branch left where
      // humanState is untouched by the failure — ERR_INVALID_CLIENT_TOKEN
      // discards the conversation entirely (humanState → ""),
      // ERR_HANDOFF_ACTIVE moves it to "active", and (Fix Round 2)
      // ERR_MODE_CLOSED moves it to "" too; all three already tore the
      // timer/offer down via applyHumanState()'s own else branch and
      // returned before reaching here. What's left is a GENUINELY
      // transient failure (500, offline, or a 429 — modeQuota is 20/hr
      // per conversation, modeIPQuota 30/min per IP) on the offer's own
      // "Go back to the assistant" tap — the offer was already removed
      // on tap (case 7) and the timer that produced it already fired and
      // nulled itself, so without this the visitor would be left
      // AI-paused with no route back and no future offer; only a page
      // reload would recover it. Re-arming (rather than re-rendering the
      // offer immediately) keeps the remove-on-tap semantics for
      // double-tap safety intact — the escape hatch simply comes back on
      // the next timeout instead of vanishing for good.
      if (this.humanState === "waiting") this.armWaitTimeout();
    } finally {
      this.setBusy(false);
    }
  }

  private renderWelcomeButtons(): void {
    const wrap = document.createElement("div");
    wrap.className = "suggestions";
    for (const b of this.welcomeButtons) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "chip";
      btn.textContent = b.label;
      btn.addEventListener("click", () => {
        // Fix Round 3, IMPORTANT: belt-and-braces for a chip rendered
        // WHILE busy (setBusy(true) only disables chips that already
        // exist at the moment it runs) — send()'s own guard already
        // covers this silently, but a silent no-op is exactly what this
        // fix removes everywhere else; a disabled chip won't dispatch
        // click at all, so this only matters for the narrow window
        // described above.
        if (this.busy) return;
        this.suggestionsEl?.remove();
        this.suggestionsEl = null;
        this.input.value = b.message;
        this.send();
      });
      wrap.appendChild(btn);
    }
    this.messagesEl.appendChild(wrap);
    this.suggestionsEl = wrap;
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private renderSuggestions(): void {
    if (!this.suggestions.length) return;
    const wrap = document.createElement("div");
    wrap.className = "suggestions";
    for (const s of this.suggestions) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "chip";
      btn.textContent = s;
      btn.addEventListener("click", () => {
        // Fix Round 3, IMPORTANT — see renderWelcomeButtons' identical
        // guard for why this exists alongside setBusy()'s disabling.
        if (this.busy) return;
        this.suggestionsEl?.remove();
        this.suggestionsEl = null;
        this.input.value = s;
        this.send();
      });
      wrap.appendChild(btn);
    }
    this.messagesEl.appendChild(wrap);
    this.suggestionsEl = wrap;
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // performChat is the single call site that sends one turn to /chat,
  // always presenting the current client token (Fix Round 1, Item 1):
  // any conversation minted WITH a token is refused by the server on
  // every later turn that omits it, not just a turn with the WRONG one.
  private async performChat(text: string): Promise<PublicChatResponse> {
    return this.chatSender(
      this.apiBase,
      this.tenant.trim(),
      this.sessionID,
      text,
      this.conversationID,
      this.widgetKey,
      undefined,
      this.resolveWidgetLocale(),
      this.clientToken,
    );
  }

  private async send(): Promise<void> {
    const text = this.input.value.trim();
    if (!text) return;
    const slug = this.tenant.trim();
    if (!slug) return;
    // Fix Round 2, IMPORTANT: shared re-entrancy guard with
    // requestHandover() — see this.busy's own field comment. Checked
    // BEFORE anything is mutated, so a blocked call leaves the composer
    // (and any in-progress draft) exactly as it was.
    if (this.busy) return;
    this.setBusy(true);
    this.input.value = "";
    this.appendMessage({ role: "user", text });
    // Strip any lingering suggestion chips once the user starts typing —
    // including an unanswered wait/continue row: the visitor has moved
    // on, and (per spec §4) typing an answer to the choice is itself a
    // valid path, classified server-side. The persistent handoff
    // affordance is deliberately NOT touched here (Fix Round 1 product
    // ruling) — its visibility is state-driven (applyHandoffAffordance),
    // not tied to the visitor typing an ordinary message.
    this.suggestionsEl?.remove();
    this.suggestionsEl = null;
    this.handoffChipsEl?.remove();
    this.handoffChipsEl = null;
    const typingEl = this.appendTyping();
    try {
      this.ensureSession();
      let resp: PublicChatResponse;
      try {
        resp = await this.performChat(text);
      } catch (e) {
        if (!(e instanceof Error) || e.message !== ERR_INVALID_CLIENT_TOKEN) throw e;
        // Fix Round 1, Item 1: a stored conversation+token pair that
        // /chat itself refuses (not just the stream) is otherwise a
        // permanent lockout — every later turn on that thread 403s
        // forever, and since Task 19 moved the conversation id to
        // localStorage, it now survives a closed tab too. Drop the
        // dead pair and retry ONCE as a brand-new thread. Any OTHER
        // 403 (rate limit, origin, a real server error) is re-thrown
        // untouched by the check above — only this exact sentinel
        // triggers a restart.
        this.discardConversationIdentity();
        resp = await this.performChat(text);
      }
      typingEl.remove();
      // resetBot / first call: server returns a different conv id.
      // Wipe the visible chat and pin the new id for subsequent
      // sends. Previous thread stays in the dashboard's
      // /conversations view.
      const returned = (resp.conversation_id ?? "").trim();
      if (returned && returned !== this.conversationID) {
        this.conversationID = returned;
        this.store(conversationKeyFor(this.tenant), returned);
        if (this.conversationID !== "" && this.messages.length > 0) {
          // Drop the on-screen history. We KEEP the last bot bubble
          // (the canned reset acknowledgement) which appendAgentReply
          // is about to render below.
          this.messages = [];
          while (this.messagesEl.firstChild) {
            this.messagesEl.removeChild(this.messagesEl.firstChild);
          }
        }
      }
      // client_token is minted ONLY on the turn that creates the
      // conversation and never echoed again (spec P2) — persist it the
      // moment it shows up.
      const token = (resp.client_token ?? "").trim();
      if (token) {
        this.clientToken = token;
        this.store(tokenKeyFor(this.tenant), token);
      }
      // Task 16: set BEFORE applyHumanState() so renderContactForm()
      // (fired synchronously from within it, on a "" → "waiting"
      // transition) sees this turn's own prompt rather than whatever an
      // earlier episode left behind — applyHumanState()'s own state
      // guard means this only actually renders on a genuine transition.
      this.contactPromptText = (resp.contact_prompt ?? "").trim();
      this.applyHumanState((resp.human_state ?? "").trim());
      // Connect once a conversation exists — see ensureStream()'s doc
      // comment. Harmless no-op until both ids are present, and
      // idempotent once the stream for this conversation is already
      // open.
      this.ensureStream();
      this.appendAgentReply(resp.text, resp.response);
      // Case 4: a /chat turn that opened (or reused) a ticket inline —
      // the visitor typed a trigger phrase, or the LLM called
      // handover_to_human — renders identically to the affordance's own
      // /handover response.
      if (resp.handoff) this.renderHandoffMessage(resp.handoff);
    } catch (e) {
      typingEl.remove();
      const msg = e instanceof Error ? e.message : String(e);
      this.appendMessage({
        role: "agent",
        text: transportErrorText(this.resolveWidgetLocale(), msg === "rate_limited"),
      });
    } finally {
      this.setBusy(false);
      this.input.focus();
    }
  }

  private appendTyping(): HTMLElement {
    const el = document.createElement("div");
    el.className = "bubble agent typing";
    el.innerHTML = "<span></span><span></span><span></span>";
    // The caller removes what we return, so hand back the wrapper — the
    // avatar has to leave with the dots.
    const turn = this.agentTurn(el);
    this.messagesEl.appendChild(turn);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    return turn;
  }

  private appendMessage(m: Message): void {
    this.messages.push(m);
    const el = document.createElement("div");
    el.className = `bubble ${m.role}`;
    // Agent and human messages may contain markdown (bold, lists, links
    // from RAG snippets or a support agent's own formatting). User input
    // is plain text — render it through the same path so the same
    // escaping rules apply to every side.
    el.innerHTML = renderMarkdown(m.text);
    if (m.role === "human") {
      // The author's name above the reply is what marks it as a real
      // person rather than the bot — prepended after the markdown body
      // so it stays a direct sibling of the rendered paragraph(s).
      const author = document.createElement("div");
      author.className = "human-author";
      author.textContent = m.author?.trim() || this.agentName;
      el.prepend(author);
    }
    this.messagesEl.appendChild(m.role === "agent" || m.role === "human" ? this.agentTurn(el) : el);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  // appendAgentReply renders one bot turn. If the runner returned a
  // structured response we render text + (single card | carousel) +
  // suggestion-chip row; otherwise we fall back to a plain bubble.
  private appendAgentReply(text: string, resp?: AgentResponse): void {
    this.messages.push({ role: "agent", text });
    const trimmed = text.trim();
    if (trimmed) {
      const bubble = document.createElement("div");
      bubble.className = "bubble agent";
      bubble.innerHTML = renderMarkdown(trimmed);
      this.messagesEl.appendChild(this.agentTurn(bubble));
    }
    if (!resp) {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
      return;
    }
    if (resp.kind === "richcard_single" && resp.richcard.length > 0) {
      this.messagesEl.appendChild(this.buildCard(resp.richcard[0], "wide"));
    } else if (resp.kind === "richcard_carousel" && resp.richcard.length > 0) {
      this.messagesEl.appendChild(this.buildCarousel(resp.richcard));
    }
    if (resp.suggestions.length > 0) {
      this.messagesEl.appendChild(this.buildChips(resp.suggestions));
    }
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private buildCard(card: AgentRichCard, variant: "wide" | "carousel"): HTMLElement {
    const article = document.createElement("article");
    article.className = `richcard ${variant}`;
    if (card.media?.url) {
      const img = document.createElement("img");
      img.className = "richcard-img";
      img.alt = "";
      img.loading = "lazy";
      img.src = card.media.url;
      // If the upstream image 404s/CORS-blocks, the <img> would
      // render as a 1px broken tile (or 0 height in some flex
      // contexts). Swap to the gradient placeholder so the card
      // never collapses on a bad URL.
      img.addEventListener("error", () => {
        const placeholder = document.createElement("div");
        placeholder.className = "richcard-placeholder";
        img.replaceWith(placeholder);
      });
      article.appendChild(img);
    } else {
      const placeholder = document.createElement("div");
      placeholder.className = "richcard-placeholder";
      article.appendChild(placeholder);
    }
    const body = document.createElement("div");
    body.className = "richcard-body";
    const title = document.createElement("h4");
    title.className = "richcard-title";
    title.textContent = card.title;
    body.appendChild(title);
    if (card.description) {
      const desc = document.createElement("p");
      desc.className = "richcard-desc";
      desc.textContent = card.description;
      body.appendChild(desc);
    }
    if (card.suggestions.length > 0) {
      const actions = document.createElement("div");
      actions.className = "richcard-actions";
      for (const s of card.suggestions.slice(0, 3)) {
        actions.appendChild(this.buildChip(s));
      }
      body.appendChild(actions);
    }
    article.appendChild(body);
    return article;
  }

  private buildCarousel(cards: AgentRichCard[]): HTMLElement {
    const scroller = document.createElement("div");
    scroller.className = "carousel";
    for (const c of cards) {
      scroller.appendChild(this.buildCard(c, "carousel"));
    }
    return scroller;
  }

  private buildChips(suggestions: AgentSuggestion[]): HTMLElement {
    const row = document.createElement("div");
    row.className = "chips";
    for (const s of suggestions) {
      row.appendChild(this.buildChip(s));
    }
    return row;
  }

  private buildChip(s: AgentSuggestion): HTMLButtonElement {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "chip";
    btn.textContent = s.text;
    btn.addEventListener("click", () => {
      // Fix Round 3, IMPORTANT: rich-card chips are never torn down the
      // way suggestionsEl/handoffChipsEl are, so one is reachable during
      // ANY in-flight send()/requestHandover() — belt-and-braces
      // alongside setBusy()'s disabling, same rationale as
      // renderWelcomeButtons'/renderSuggestions' identical guard.
      if (this.busy) return;
      if (s.kind === "action" && s.action === "url" && s.to) {
        window.open(s.to, "_blank", "noopener,noreferrer");
        return;
      }
      const payload = (s.postback || s.text).trim();
      if (!payload) return;
      this.input.value = payload;
      void this.send();
    });
    return btn;
  }
}
